import React, { useEffect, useRef, useState } from 'react';
import Card from 'components/card';
import Switch from 'components/switch';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import { MdChevronLeft, MdChevronRight, MdClose, MdPayment, MdBlock, MdExpandMore, MdExpandLess, MdVerifiedUser, MdCheckCircle, MdCancel, MdFileDownload, MdContentCopy, MdCheck, MdSearch, MdShoppingCart, MdForward, MdBugReport } from 'react-icons/md';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { frenchAreas } from 'variables/industries';
import BatiwebForwardCell, { BatiwebForward, BatiwebForwardState, STATES as BATIWEB_STATES } from './BatiwebForwardCell';
import SearchSelect, { SearchSelectOption } from 'components/fields/SearchSelect';
import { productOptions } from 'views/backoffice/productMismatches/ProductPicker';
import MarketplaceVisibilityCell, { MarketplaceVisibility } from './MarketplaceVisibilityCell';
import WaterfallJourneyCell, { LeadJourney } from './WaterfallJourneyCell';
import RowActions, { RowAction } from './RowActions';
import { userDisplayName } from 'utils/userName';

// États Batiweb qui attendent une action (envoyer, retester, corriger le produit) : actions affichées.
const BATIWEB_ACTION_STATES: BatiwebForwardState[] = ['not_sent', 'not_sellable', 'error', 'no_subindustry', 'unknown_subindustry'];

// Region filter options (available on every tab): climate zones (H1/H2/H3) plus every French
// department, i.e. frenchAreas minus the whole-country/whole-country-abroad entries — "Toute
// la France" is the filter's own default (empty selection), not a pickable option here.
// Filtre « Statut Batiweb » de l'onglet No match, dans l'ordre du parcours d'un lead. Pas de
// « Vendu » : le cron approuve un lead vendu dans la foulée, il quitte alors l'onglet No match.
const BATIWEB_FILTER_GROUPS: { group: string; states: BatiwebForwardState[] }[] = [
    { group: 'Envoyés à Batiweb', states: ['pending', 'sending', 'unsold', 'not_sellable', 'error'] },
    { group: 'Jamais envoyés', states: ['not_sent', 'to_configure', 'unknown_subindustry', 'no_subindustry', 'no_equivalent', 'hidden'] },
];
const BATIWEB_FILTER_OPTIONS: SearchSelectOption[] = BATIWEB_FILTER_GROUPS.flatMap(({ group, states }) =>
    states.map((state) => ({ value: state, label: BATIWEB_STATES[state].label, group }))
);

const NO_MATCH_REGION_OPTIONS = frenchAreas.filter((area) => !area.toLowerCase().includes('toute'));

// Normalizes industry/subIndustry labels for comparison so "panneaux-solaires" and "Panneaux solaires" match
const normalizeIndustryLabel = (str?: string) => {
    if (!str) return '';
    return str
        .toLowerCase()
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '')
        .replace(/[-_]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
};

interface RunningOffer {
    _id: string;
    name: string;
    offerType: 'marketplace' | 'direct' | 'purchaseOrder';
    sellerPrice: number;
    buyerPrice: number;
    leadsGenerated: number;
    weeklyQuantity: string;
    offerNumber?: string;
    industry?: string;
    subIndustry?: string;
    isAutoSuspended?: boolean;
    leadApproved?: number;
    leadPending?: number;
    leadRejected?: number;
    // PurchaseOrder specific
    maxPricePerLead?: number;
    totalLeadsDelivered?: number;
    leadCount?: number;
    buyer: {
        _id: string;
        name: string;
        surname: string;
        email: string;
        company?: string;
    } | null;
}

interface Lead {
    _id: string;
    sellerId: {
        _id: string;
        name?: string;
        surname?: string;
        company?: string;
        email: string;
        alreadyPaidAmount?: number;
    } | null;
    buyerId: {
        _id: string;
        name?: string;
        surname?: string;
        company?: string;
        email: string;
    } | null;
    sideSellerId?: {
        _id: string;
        name?: string;
        surname?: string;
        company?: string;
        email: string;
        alreadyPaidAmount?: number;
    } | null;
    orderNumber: string;
    leadQualityStatus?: 'valid' | 'invalid' | 'no_match' | 'blocked' | 'test' | 'duplicate';
    invalidReason?: string;
    // Set by the back's unsold-lead cron: still no_match 7 days after creation ("Invendus" tab).
    unsoldAt?: string | null;
    // Deduplication keys (see leadIdentity in the back): what a 'duplicate' lead was matched on.
    emailNormalized?: string | null;
    phoneNormalized?: string | null;
    channels?: string[];
    status: string;
    approvalStatus?: 'pending' | 'approved' | 'rejected' | 'conflict';
    leadrsRate: number;
    customerRate: number;
    comment?: string;
    leadPrice?: number;
    buyerPrice?: number;
    paidAt?: string;
    createdAt: string;
    marginBreakdown?: {
        total: number;
        base10: number;
        sellerCoupon: number;
        sellerCouponCode?: string;
        buyerCoupon: number;
        buyerCouponCode?: string;
    };
    body: Array<{ field: string; answer: string }>;
    ip?: string;
    country?: string;
    city?: string;
    // Distance (km) between the submission IP's geolocation and the lead's declared postal
    // code, computed server-side; null/undefined when either side couldn't be resolved.
    ipDistanceKm?: number | null;
    source?: string;
    // Landing page URL the lead was submitted from — admin-only, never sent to buyer/seller.
    landingPage?: string;
    // RGPD proof-of-consent captured from the iframe form's opt-in checkbox.
    gdprConsent?: {
        accepted: boolean;
        text?: string;
        consentedAt?: string;
        userAgent?: string;
        language?: string;
        hostName?: string;
        pageUrl?: string;
        ip?: string;
        // TrustedForm certificate, retained by the back's trustedFormCron within 72h of submission.
        trustedFormCertUrl?: string;
        trustedFormRetainedAt?: string;
        trustedFormExpiresAt?: string;
        trustedFormRetainAttempts?: number;
        trustedFormRetainError?: string;
    };
    // Set on no_match leads: the industry the lead was routed under (only source for universal templates).
    industry?: string;
    subIndustry?: string;
    offerId?: {
        industry?: string;
        subIndustry?: string;
        name?: string;
    } | null;
    formTemplateId?: {
        industry?: string;
        subIndustry?: string;
        name?: string;
    } | null;
    purchaseOrderId?: {
        industry?: string;
        subIndustry?: string;
        name?: string;
    } | null;
    clonedToLeadId?: string;
    // No-match tab only: the seller's expected price, from the LeadMatchDiagnostic recorded at
    // ingestion — drives the "Forcer l'achat" CTA. Undefined when no diagnostic is available
    // (e.g. it expired, or the lead's industry was never resolved).
    forceBuyPrice?: number;
    // Set once this lead was bought internally by Leadrs (not a real buyer) via "Forcer l'achat" —
    // admin-only, never shown to the seller.
    isForceBought?: boolean;
    // Revente à HelloArtisan / Batiweb — admin-only. 'sold' = acheté par Batiweb (pas de buyerId).
    helloArtisan?: {
        status: 'sending' | 'not_sellable' | 'pending' | 'sold' | 'unsold' | 'error';
        worktypeLabel?: string;
    } | null;
    // No-match tab only: what happened to the lead on the Batiweb side, or why it was never sent.
    batiwebForward?: BatiwebForward | null;
    // Étape du waterfall et ce qui est arrivé au lead (onglet No match).
    journey?: LeadJourney | null;
    // No-match tab only: is the lead listed on the Bourse du Lead, and if not, why.
    marketplace?: MarketplaceVisibility | null;
}

interface MatchCandidateCheck {
    pass: boolean;
    // The offer/PO's own value for this attribute.
    offerValue?: any;
    // What offerValue needed to be for a match — the lead/seller's value for filters that
    // compare against the lead, or a fixed platform requirement otherwise (see leadLabel).
    leadValue?: any;
    // What leadValue represents: "Lead", "Vendeur", "Requis", "Limite", etc.
    leadLabel?: string;
}

interface MatchCandidate {
    offerType: 'Offer' | 'SellerOffer' | 'PurchaseOrder';
    offerId: string;
    offerNumber?: string;
    name?: string;
    ownerId?: string;
    status?: string;
    isAutoSuspended: boolean;
    buyerPrice?: number;
    buyerEmail?: string | null;
    checks: Record<string, MatchCandidateCheck>;
    failedFilters: string[];
    priceGap?: number | null;
}

interface LeadMatchDiagnostic {
    _id: string;
    criteria: {
        industry: string;
        subIndustry?: string;
        region?: string;
        channels?: string[];
        minimumPrice?: number;
        industryRecognized?: boolean;
    };
    candidates: MatchCandidate[];
    closestMiss?: { offerType: string; offerId: string; failedFilters: string[] } | null;
    createdAt: string;
}

// Submission metadata that historical leads may have picked up as a stray body entry (fixed
// at the source in leadCatcher's getAnswers, but older leads can still carry one) — filtered
// out here so it doesn't show up duplicated next to the dedicated "Origine" card fields.
const RESERVED_BODY_FIELDS = new Set(['sellerid', 'offerid', 'templateid', 'sidesellerid', 'landingpage']);
const visibleLeadBody = (body: Lead['body']) => (body || []).filter((item) => !RESERVED_BODY_FIELDS.has((item.field || '').toLowerCase()));

// Admin-only display label for lead.source (submission channel) — never shown to buyer/seller.
const LEAD_SOURCE_LABELS: Record<string, string> = {
    iframe: 'Formulaire (iframe)',
    api: 'API',
    'google-sheet': 'Google Sheet',
};
const leadSourceLabel = (source?: string): string => (source ? LEAD_SOURCE_LABELS[source] || source : 'Inconnu');

const LEADS_TAB_LAST_SEEN_STORAGE_KEY = 'backOfficeLeadsLastSeen';
// TrustedForm certificate state: retained (valid, kept until expiresAt), pending (cron hasn't
// retained it yet), failed (TrustedForm refused it — see the error) or expired (72h window passed).
const TRUSTED_FORM_WINDOW_MS = 72 * 60 * 60 * 1000;
const TrustedFormBadge = ({ consent, createdAt }: { consent: NonNullable<Lead['gdprConsent']>; createdAt: string }) => {
    const expired = Date.now() - new Date(createdAt).getTime() > TRUSTED_FORM_WINDOW_MS;
    const state = consent.trustedFormRetainedAt
        ? { label: 'Valide', className: 'text-green-600 dark:text-green-400', icon: <MdCheckCircle className="h-4 w-4 shrink-0 text-green-500" /> }
        : consent.trustedFormRetainError && (expired || (consent.trustedFormRetainAttempts || 0) >= 5)
          ? { label: 'Échec', className: 'text-red-600 dark:text-red-400', icon: <MdCancel className="h-4 w-4 shrink-0 text-red-500" /> }
          : expired
            ? { label: 'Expiré', className: 'text-gray-500 dark:text-gray-400', icon: <MdCancel className="h-4 w-4 shrink-0 text-gray-400" /> }
            : { label: 'En attente', className: 'text-amber-600 dark:text-amber-400', icon: null };
    const title = [
        consent.trustedFormRetainedAt && `Conservé le ${new Date(consent.trustedFormRetainedAt).toLocaleString('fr-FR')}`,
        consent.trustedFormExpiresAt && `Valable jusqu'au ${new Date(consent.trustedFormExpiresAt).toLocaleDateString('fr-FR')}`,
        consent.trustedFormRetainError && `Erreur : ${consent.trustedFormRetainError}`,
        consent.trustedFormCertUrl,
    ]
        .filter(Boolean)
        .join('\n');
    return (
        <a
            href={consent.trustedFormCertUrl}
            target="_blank"
            rel="noopener noreferrer"
            title={title}
            className="inline-flex items-center gap-1.5 self-start rounded-full border border-gray-200 bg-white px-2.5 py-1 text-xs hover:border-gray-400 dark:border-navy-600 dark:bg-navy-800"
        >
            <MdVerifiedUser className="h-4 w-4 shrink-0 text-gray-400 dark:text-gray-500" />
            <span className="font-medium text-gray-700 dark:text-gray-300">TrustedForm</span>
            <span className={state.className}>{state.label}</span>
            {state.icon}
        </a>
    );
};

const LEADS_TAB_KEYS = ['all', 'pending', 'conflict', 'approved', 'rejected', 'paid', 'invalid', 'no_match', 'blocked', 'duplicate', 'debug'] as const;
const NON_QUALIFIED_TAB_VALUES = ['invalid', 'no_match', 'unsold', 'blocked', 'duplicate'];
// "Invendus" = No match leads the back's unsold-lead cron has marked (unsoldAt, 7 days after
// creation; the seller sees them as "Invendu"). Same leads, same layout and tools as No match.
const isNoMatchLikeTab = (tab: string) => tab === 'no_match' || tab === 'unsold';

const REJECTION_REASONS = [
    'Numéro de téléphone invalide',
    'Numéro injoignable',
    'Coordonnées incomplètes',
    'Informations incohérentes',
    "Le prospect n'a jamais effectué cette demande",
    'Prospect non intéressé',
    'Demande réalisée par erreur',
    'Type de projet incorrect',
    'Travaux déjà réalisés',
    'Projet trop éloigné dans le temps',
    'Aucune intention de réaliser les travaux',
    'Doublon détecté',
    'Coordonnées suspectes ou fictives',
    'Comportement frauduleux détecté',
    'Consentement RGPD absent',
];

type RejectModalState =
    | { type: 'adminReject'; leadId: string }
    | { type: 'rejectAccepted'; lead: Lead }
    | { type: 'bulkRejectApproved' }
    | { type: 'bulkRejectSelected' };

export default function BackOfficeLeads() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const initialSearch = searchParams.get('search') || '';
    const [leads, setLeads] = useState<Lead[]>([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [tabFilter, setTabFilter] = useState<any>('all');
    const [expandedLeadIds, setExpandedLeadIds] = useState<Set<string>>(new Set());
    const [searchInput, setSearchInput] = useState(initialSearch);
    const [searchQuery, setSearchQuery] = useState(initialSearch);
    const [isForwardModalOpen, setIsForwardModalOpen] = useState(false);
    const [forwardingLead, setForwardingLead] = useState<Lead | null>(null);
    const [availableOffers, setAvailableOffers] = useState<RunningOffer[]>([]);
    const [selectedOfferId, setSelectedOfferId] = useState('');
    const [selectedOfferType, setSelectedOfferType] = useState('');
    const [forwardSameSubIndustryOnly, setForwardSameSubIndustryOnly] = useState(true);
    const [isForwarding, setIsForwarding] = useState(false);
    const [payingLeadId, setPayingLeadId] = useState<string | null>(null);
    const [forceBuyingLeadId, setForceBuyingLeadId] = useState<string | null>(null);
    const [markingAsPaidLeadId, setMarkingAsPaidLeadId] = useState<string | null>(null);
    const [isBulkPaying, setIsBulkPaying] = useState(false);
    const [isBulkMarkingAsPaid, setIsBulkMarkingAsPaid] = useState(false);
    const [isBulkRejecting, setIsBulkRejecting] = useState(false);
    const [rejectModal, setRejectModal] = useState<RejectModalState | null>(null);
    const [rejectReason, setRejectReason] = useState('');
    const [copiedLeadId, setCopiedLeadId] = useState<string | null>(null);
    const [debugLead, setDebugLead] = useState<Lead | null>(null);
    const [debugDiagnostic, setDebugDiagnostic] = useState<LeadMatchDiagnostic | null>(null);
    const [debugLoading, setDebugLoading] = useState(false);
    const [debugError, setDebugError] = useState<string | null>(null);
    const [selectedLeadIds, setSelectedLeadIds] = useState<string[]>([]);
    const [commissionStats, setCommissionStats] = useState<{ days7: number; days30: number; days90: number } | null>(null);
    const [industryFilter, setIndustryFilter] = useState('');
    const [subIndustryFilter, setSubIndustryFilter] = useState('');
    const [batiwebStateFilter, setBatiwebStateFilter] = useState('');
    // Empty array = "Toute la France" (default, no region filter applied)
    const [regionFilter, setRegionFilter] = useState<string[]>([]);
    const [regionSearchInput, setRegionSearchInput] = useState('');
    const [isRegionDropdownOpen, setIsRegionDropdownOpen] = useState(false);
    const regionDropdownRef = useRef<HTMLDivElement>(null);
    const [isExportingCsv, setIsExportingCsv] = useState(false);
    const [availableIndustries, setAvailableIndustries] = useState<{ name: string; subIndustries: { name: string }[] }[]>([]);
    const [tabLastSeen, setTabLastSeen] = useState<Record<string, string>>({});
    const [tabNewCounts, setTabNewCounts] = useState<Record<string, number>>({});

    useEffect(() => {
        fetchLeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, tabFilter, searchQuery, industryFilter, subIndustryFilter, batiwebStateFilter, regionFilter.join(',')]);

    useEffect(() => {
        setSelectedLeadIds([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, tabFilter, searchQuery, industryFilter, subIndustryFilter, batiwebStateFilter, regionFilter.join(',')]);

    // Close the region dropdown on outside click
    useEffect(() => {
        const handleClick = (e: MouseEvent) => {
            if (regionDropdownRef.current && !regionDropdownRef.current.contains(e.target as Node)) {
                setIsRegionDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    useEffect(() => {
        const token = localStorage.getItem('token');
        axios.get(`${CONFIG.apiUrl}/admin/leads/commission-stats`, {
            headers: { Authorization: `Bearer ${token}` },
        }).then((res) => setCommissionStats(res.data)).catch(() => {});
    }, []);

    // Initialize per-tab "last seen" timestamps from localStorage (first visit to a tab defaults to now, i.e. no new leads yet)
    useEffect(() => {
        let stored: Record<string, string> = {};
        try {
            stored = JSON.parse(localStorage.getItem(LEADS_TAB_LAST_SEEN_STORAGE_KEY) || '{}');
        } catch {
            stored = {};
        }
        const now = new Date().toISOString();
        const merged: Record<string, string> = { ...stored };
        let changed = false;
        LEADS_TAB_KEYS.forEach((key) => {
            if (!merged[key]) {
                merged[key] = now;
                changed = true;
            }
        });
        if (changed) localStorage.setItem(LEADS_TAB_LAST_SEEN_STORAGE_KEY, JSON.stringify(merged));
        setTabLastSeen(merged);
    }, []);

    const fetchTabNewCounts = async (seenMap: Record<string, string>) => {
        if (Object.keys(seenMap).length === 0) return;
        try {
            const token = localStorage.getItem('token');
            const params: any = {};
            if (seenMap.all) params.sinceAll = seenMap.all;
            if (seenMap.pending) params.sincePending = seenMap.pending;
            if (seenMap.conflict) params.sinceConflict = seenMap.conflict;
            if (seenMap.approved) params.sinceApproved = seenMap.approved;
            if (seenMap.rejected) params.sinceRejected = seenMap.rejected;
            if (seenMap.paid) params.sincePaid = seenMap.paid;
            if (seenMap.invalid) params.sinceInvalid = seenMap.invalid;
            if (seenMap.no_match) params.sinceNoMatch = seenMap.no_match;
            if (seenMap.blocked) params.sinceBlocked = seenMap.blocked;
            if (seenMap.duplicate) params.sinceDuplicate = seenMap.duplicate;
            if (seenMap.debug) params.sinceDebug = seenMap.debug;
            const res = await axios.get(`${CONFIG.apiUrl}/admin/leads/new-counts`, {
                headers: { Authorization: `Bearer ${token}` },
                params,
            });
            setTabNewCounts(res.data.counts || {});
        } catch {
            // Non-critical: leave badges as-is on error
        }
    };

    // Poll new-lead counts for the red tab badges
    useEffect(() => {
        if (Object.keys(tabLastSeen).length === 0) return;
        fetchTabNewCounts(tabLastSeen);
        const interval = setInterval(() => fetchTabNewCounts(tabLastSeen), 30000);
        return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tabLastSeen]);

    const handleTabClick = (tabValue: string) => {
        setTabFilter(tabValue);
        setPage(1);
        const now = new Date().toISOString();
        setTabLastSeen((prev) => {
            const updated = { ...prev, [tabValue]: now };
            localStorage.setItem(LEADS_TAB_LAST_SEEN_STORAGE_KEY, JSON.stringify(updated));
            return updated;
        });
        setTabNewCounts((prev) => ({ ...prev, [tabValue]: 0 }));
    };

    useEffect(() => {
        // Secteur / produit filtrent tous les onglets ; le statut Batiweb n'existe que sur No match.
        if (availableIndustries.length === 0) {
            const token = localStorage.getItem('token');
            axios.get(`${CONFIG.apiUrl}/industry`, {
                headers: { Authorization: `Bearer ${token}` },
            }).then((res) => setAvailableIndustries(res.data)).catch(() => {});
        }
        if (!isNoMatchLikeTab(tabFilter)) setBatiwebStateFilter('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tabFilter]);

    const fetchLeads = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const params: any = { page, limit: 50 };
            if (searchQuery) params.search = searchQuery;
            if (regionFilter.length) params.region = regionFilter;
            if (industryFilter) params.industry = industryFilter;
            if (subIndustryFilter) params.subIndustry = subIndustryFilter;
            if (isNoMatchLikeTab(tabFilter) && batiwebStateFilter) params.batiwebState = batiwebStateFilter;
            if (tabFilter === 'paid') {
                params.paid = 'true';
            } else if (tabFilter === 'pending') {
                params.approvalStatus = 'pending';
            } else if (tabFilter === 'approved') {
                params.approvalStatus = 'approved';
            } else if (tabFilter === 'rejected') {
                params.approvalStatus = 'rejected';
            } else if (NON_QUALIFIED_TAB_VALUES.includes(tabFilter)) {
                params.qualityStatus = 'non_qualified';
                params.qualityReason = tabFilter;
            } else if (tabFilter === 'conflict') {
                params.approvalStatus = 'conflict';
            } else if (tabFilter === 'debug') {
                params.qualityStatus = 'debug';
            }

            const response = await axios.get(`${CONFIG.apiUrl}/admin/leads`, {
                headers: { Authorization: `Bearer ${token}` },
                params
            });
            
            setLeads(response.data.leads);
            setTotal(response.data.total);
            setTotalPages(response.data.pages);
        } catch (error) {
            console.error('Error fetching leads:', error);
        } finally {
            setLoading(false);
        }
    };

    // Pulls every no-match lead matching the current secteur/sous-secteur/région filters in one
    // unpaginated request (using the count already loaded via fetchLeads as the limit) instead of
    // the 50-per-page table, so the CSV reflects the full filtered set.
    const handleExportNoMatchCsv = async () => {
        setIsExportingCsv(true);
        try {
            const token = localStorage.getItem('token');
            const params: any = {
                page: 1,
                limit: Math.max(total, 1),
                qualityStatus: 'non_qualified',
                qualityReason: tabFilter,
                industry: industryFilter,
                subIndustry: subIndustryFilter,
                region: regionFilter,
            };
            if (searchQuery) params.search = searchQuery;
            if (batiwebStateFilter) params.batiwebState = batiwebStateFilter;

            const response = await axios.get(`${CONFIG.apiUrl}/admin/leads`, {
                headers: { Authorization: `Bearer ${token}` },
                params,
            });
            const exportLeads: Lead[] = response.data.leads || [];
            if (exportLeads.length === 0) {
                alert('Aucun lead à exporter pour ces filtres.');
                return;
            }

            const bodyFields = Array.from(
                exportLeads.reduce((set, lead) => {
                    visibleLeadBody(lead.body).forEach((item) => { if (item.field) set.add(item.field); });
                    return set;
                }, new Set<string>())
            );
            const headers = ['N° commande', 'Date', 'Secteur', 'Produit', ...bodyFields, 'Email vendeur', 'Ville', 'Pays'];
            const rows = exportLeads.map((lead) => {
                const industry = lead.industry || lead.offerId?.industry || lead.purchaseOrderId?.industry || lead.formTemplateId?.industry || '';
                const subIndustry = lead.subIndustry || lead.offerId?.subIndustry || lead.purchaseOrderId?.subIndustry || lead.formTemplateId?.subIndustry || '';
                const leadBody = visibleLeadBody(lead.body);
                const bodyValues = bodyFields.map((field) => leadBody.find((item) => item.field === field)?.answer || '');
                return [
                    lead.orderNumber || lead._id,
                    new Date(lead.createdAt).toLocaleString('fr-FR'),
                    industry,
                    subIndustry,
                    ...bodyValues,
                    lead.sellerId?.email || '',
                    lead.city || '',
                    lead.country || '',
                ];
            });
            const csvContent = [headers, ...rows]
                .map((row) => row.map((field) => (typeof field === 'string' ? `"${field.replace(/"/g, '""')}"` : field)).join(','))
                .join('\n');
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.setAttribute('href', url);
            link.setAttribute('download', `leads_${tabFilter}_${new Date().toISOString().slice(0, 10)}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error exporting no-match leads:', error);
            alert("Erreur lors de l'export CSV");
        } finally {
            setIsExportingCsv(false);
        }
    };

    const getStatusColor = (status: string) => {
        switch (status?.toLowerCase()) {
            case 'validated':
            case 'paid':
                return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
            case 'pending':
            case 'waiting':
                return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
            case 'rejected':
            case 'refused':
                return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
            default:
                return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
        }
    };

    const getApprovalStatusColor = (status?: string) => {
        switch (status?.toLowerCase()) {
            case 'approved':
                return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
            case 'pending':
                return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
            case 'conflict':
                return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 font-bold';
            case 'rejected':
                return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
            default:
                return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
        }
    };

    const handleApprovalStatusChange = async (leadId: string, newStatus: 'approved' | 'rejected') => {
        try {
            const token = localStorage.getItem('token');
            await axios.put(
                `${CONFIG.apiUrl}/admin/leads/${leadId}/approval-status`,
                { approvalStatus: newStatus },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            // Refresh leads after update
            fetchLeads();
        } catch (error) {
            console.error('Error updating approval status:', error);
            alert('Failed to update approval status');
        }
    };

    const handleAdminReject = (leadId: string) => {
        setRejectReason('');
        setRejectModal({ type: 'adminReject', leadId });
    };

    const handleRejectAccepted = (lead: Lead) => {
        setRejectReason('');
        setRejectModal({ type: 'rejectAccepted', lead });
    };

    const closeRejectModal = () => {
        setRejectModal(null);
        setRejectReason('');
    };

    const submitRejectModal = async () => {
        if (!rejectModal || !rejectReason) return;

        if (rejectModal.type === 'adminReject') {
            try {
                const token = localStorage.getItem('token');
                await axios.put(
                    `${CONFIG.apiUrl}/lead/${rejectModal.leadId}/admin-reject`,
                    { rejectionReason: rejectReason },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                closeRejectModal();
                fetchLeads();
            } catch (error) {
                console.error('Error rejecting lead:', error);
                alert('Failed to reject lead');
            }
            return;
        }

        if (rejectModal.type === 'rejectAccepted') {
            const lead = rejectModal.lead;
            if (lead.paidAt && !window.confirm('Confirmer l\'annulation du paiement et le rejet du lead ?')) return;
            try {
                const token = localStorage.getItem('token');
                await axios.post(
                    `${CONFIG.apiUrl}/admin/leads/${lead._id}/reject-accepted`,
                    { rejectionReason: rejectReason },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                closeRejectModal();
                fetchLeads();
            } catch (error: any) {
                console.error('Error rejecting accepted lead:', error);
                alert(error?.response?.data?.message || 'Erreur lors du rejet du lead');
            }
            return;
        }

        if (rejectModal.type === 'bulkRejectApproved') {
            if (selectedLeads.length === 0) return;
            setIsBulkRejecting(true);
            try {
                const token = localStorage.getItem('token');
                await Promise.all(
                    selectedLeads.map((lead) =>
                        axios.post(
                            `${CONFIG.apiUrl}/admin/leads/${lead._id}/reject-accepted`,
                            { rejectionReason: rejectReason },
                            { headers: { Authorization: `Bearer ${token}` } }
                        )
                    )
                );
                alert(`${selectedLeads.length} lead(s) rejeté(s).`);
                setSelectedLeadIds([]);
                closeRejectModal();
                fetchLeads();
            } catch (error: any) {
                alert(error?.response?.data?.message || 'Erreur lors du rejet en masse');
            } finally {
                setIsBulkRejecting(false);
            }
            return;
        }

        if (rejectModal.type === 'bulkRejectSelected') {
            if (selectedRejectableLeads.length === 0) return;
            setIsBulkRejecting(true);
            try {
                const token = localStorage.getItem('token');
                await Promise.all(
                    selectedRejectableLeads.map((lead) =>
                        axios.put(
                            `${CONFIG.apiUrl}/lead/${lead._id}/admin-reject`,
                            { rejectionReason: rejectReason },
                            { headers: { Authorization: `Bearer ${token}` } }
                        )
                    )
                );
                alert(`${selectedRejectableLeads.length} lead(s) rejeté(s).`);
                setSelectedLeadIds([]);
                closeRejectModal();
                fetchLeads();
            } catch (error: any) {
                alert(error?.response?.data?.message || 'Erreur lors du rejet en masse');
            } finally {
                setIsBulkRejecting(false);
            }
            return;
        }
    };

    const toggleLeadDetails = (leadId: string) => {
        setExpandedLeadIds((prev) => {
            const next = new Set(prev);
            if (next.has(leadId)) next.delete(leadId);
            else next.add(leadId);
            return next;
        });
    };

    const handleOpenForward = async (lead: Lead) => {
        setForwardingLead(lead);
        setSelectedOfferId('');
        setSelectedOfferType('');
        setForwardSameSubIndustryOnly(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${CONFIG.apiUrl}/admin/offers/running`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setAvailableOffers(response.data.offers);
        } catch (error) {
            console.error('Error fetching running offers:', error);
            alert('Impossible de charger les offres disponibles');
            return;
        }
        setIsForwardModalOpen(true);
    };

    const handlePayLead = async (lead: Lead) => {
        setPayingLeadId(lead._id);
        try {
            const token = localStorage.getItem('token');

            // Determine payee and fetch their pre-paid balance
            const hasSideSeller = lead.sideSellerId && lead.sideSellerId._id !== lead.sellerId?._id;
            const payeeId = hasSideSeller ? lead.sideSellerId!._id : lead.sellerId?._id;
            let confirmMsg = 'Confirmer le paiement de ce lead ?';
            if (payeeId) {
                try {
                    const userRes = await axios.get(`${CONFIG.apiUrl}/admin/users/${payeeId}`, {
                        headers: { Authorization: `Bearer ${token}` },
                    });
                    const balance: number = userRes.data.prePaidBalance || 0;
                    if (balance > 0) {
                        const leadPrice = lead.leadPrice || 0;
                        const balanceUsed = Math.min(balance, leadPrice);
                        const stripeAmount = leadPrice - balanceUsed;
                        confirmMsg += `\n\nSolde pré-payé vendeur : €${balance.toFixed(2)}\nLead : €${leadPrice.toFixed(2)}`;
                        confirmMsg += stripeAmount > 0
                            ? `\n→ €${balanceUsed.toFixed(2)} déduit du solde + €${stripeAmount.toFixed(2)} via Stripe`
                            : `\n→ €${balanceUsed.toFixed(2)} déduit du solde (sans Stripe)`;
                    }
                } catch { /* ignore if user fetch fails */ }
            }

            if (!window.confirm(confirmMsg)) {
                setPayingLeadId(null);
                return;
            }

            await axios.post(
                `${CONFIG.apiUrl}/admin/leads/${lead._id}/pay`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            fetchLeads();
        } catch (error: any) {
            alert(error?.response?.data?.message || 'Erreur lors du paiement');
        } finally {
            setPayingLeadId(null);
        }
    };

    const handleForceBuy = async (lead: Lead) => {
        const price = lead.forceBuyPrice || 0;
        const confirmMsg = `Forcer l'achat de ce lead pour €${price.toFixed(2)} ?\n\nLe vendeur sera payé immédiatement, comme si un acheteur l'avait acheté. Aucun acheteur réel ne sera notifié.`;
        if (!window.confirm(confirmMsg)) return;

        setForceBuyingLeadId(lead._id);
        try {
            const token = localStorage.getItem('token');
            await axios.post(
                `${CONFIG.apiUrl}/admin/leads/${lead._id}/force-buy`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
        } catch (error: any) {
            alert(error?.response?.data?.message || "Erreur lors du forçage de l'achat");
        } finally {
            // Even when the payment leg fails, the endpoint has already flipped the lead out of
            // no_match (approved/priced, payable later via the normal "Payer" button) — refetch
            // regardless of outcome so the list doesn't show it stuck under "No match".
            setForceBuyingLeadId(null);
            fetchLeads();
        }
    };

    // invalidReason says which identity matched ("même email" / "même téléphone"); searching it
    // from the "Dupliqués" tab lists every other duplicate of that person.
    const duplicateSearchValue = (lead: Lead) =>
        (lead.invalidReason?.includes('téléphone') ? lead.phoneNormalized : lead.emailNormalized)
        || lead.emailNormalized || lead.phoneNormalized || null;

    const handleShowDuplicates = (value: string) => {
        setSearchInput(value);
        setSearchQuery(value);
        setPage(1);
    };

    const handleCopyLeadId = (id: string) => {
        navigator.clipboard.writeText(id);
        setCopiedLeadId(id);
        setTimeout(() => setCopiedLeadId((prev) => (prev === id ? null : prev)), 1500);
    };

    const handleOpenDebug = async (lead: Lead) => {
        setDebugLead(lead);
        setDebugDiagnostic(null);
        setDebugError(null);
        setDebugLoading(true);
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`${CONFIG.apiUrl}/admin/leads/${lead._id}/match-diagnostic`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setDebugDiagnostic(res.data);
        } catch (error: any) {
            if (error?.response?.status === 404) {
                setDebugError("Pas de diagnostic disponible pour ce lead (expiré ou non calculé).");
            } else {
                setDebugError(error?.response?.data?.message || 'Erreur lors du chargement du diagnostic');
            }
        } finally {
            setDebugLoading(false);
        }
    };

    const closeDebugModal = () => {
        setDebugLead(null);
        setDebugDiagnostic(null);
        setDebugError(null);
    };

    const formatCheckValue = (v: any): string => {
        if (v === undefined || v === null || v === '') return '-';
        if (Array.isArray(v)) return v.length ? v.join(', ') : '(aucun)';
        return String(v);
    };

    // PurchaseOrder never uses the literal 'running' status (its equivalent is 'active').
    const isRunningCandidate = (c: MatchCandidate) =>
        c.offerType === 'PurchaseOrder' ? c.status === 'active' : c.status === 'running';

    const getLeadPayee = (lead: Lead) => {
        const hasSideSeller = lead.sideSellerId && lead.sideSellerId._id !== lead.sellerId?._id;
        return hasSideSeller ? lead.sideSellerId : lead.sellerId;
    };

    const handleMarkAsPaid = async (lead: Lead) => {
        const payee = getLeadPayee(lead);
        const available = payee?.alreadyPaidAmount ?? 0;
        const leadPrice = lead.leadPrice || 0;
        const remaining = Math.max(0, available - leadPrice);
        const confirmMsg = `Marquer ce lead comme payé sans virement Stripe.\n\nSolde vendeur : €${available.toFixed(2)}\nPrix lead : €${leadPrice.toFixed(2)}\n→ Déduit : €${leadPrice.toFixed(2)} — Restant : €${remaining.toFixed(2)}`;
        if (!window.confirm(confirmMsg)) return;

        setMarkingAsPaidLeadId(lead._id);
        try {
            const token = localStorage.getItem('token');
            await axios.post(
                `${CONFIG.apiUrl}/admin/leads/${lead._id}/mark-as-paid`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            fetchLeads();
        } catch (error: any) {
            alert(error?.response?.data?.message || 'Erreur lors du marquage');
        } finally {
            setMarkingAsPaidLeadId(null);
        }
    };

    const isLeadBulkPayEligible = (lead: Lead) => {
        return lead.approvalStatus === 'approved' && !lead.paidAt && !!lead.leadPrice && lead.leadPrice > 0;
    };

    const isLeadBulkRejectEligible = (lead: Lead) => {
        return lead.approvalStatus === 'pending';
    };

    const toggleLeadSelection = (leadId: string) => {
        setSelectedLeadIds((prev) => {
            if (prev.includes(leadId)) return prev.filter((id) => id !== leadId);
            return [...prev, leadId];
        });
    };

    const payableLeadIdsOnPage = tabFilter === 'approved'
        ? leads.filter(isLeadBulkPayEligible).map((lead) => lead._id)
        : [];

    const rejectableLeadIdsOnPage = tabFilter === 'pending'
        ? leads.filter(isLeadBulkRejectEligible).map((lead) => lead._id)
        : [];

    const allPayableSelected = payableLeadIdsOnPage.length > 0 && payableLeadIdsOnPage.every((id) => selectedLeadIds.includes(id));
    const allRejectableSelected = rejectableLeadIdsOnPage.length > 0 && rejectableLeadIdsOnPage.every((id) => selectedLeadIds.includes(id));

    const toggleSelectAllPayable = () => {
        if (allPayableSelected) {
            setSelectedLeadIds((prev) => prev.filter((id) => !payableLeadIdsOnPage.includes(id)));
            return;
        }

        setSelectedLeadIds((prev) => [...new Set([...prev, ...payableLeadIdsOnPage])]);
    };

    const toggleSelectAllRejectable = () => {
        if (allRejectableSelected) {
            setSelectedLeadIds((prev) => prev.filter((id) => !rejectableLeadIdsOnPage.includes(id)));
            return;
        }

        setSelectedLeadIds((prev) => [...new Set([...prev, ...rejectableLeadIdsOnPage])]);
    };

    const selectedLeads = leads.filter((lead) => selectedLeadIds.includes(lead._id) && isLeadBulkPayEligible(lead));
    const selectedPayoutTotal = selectedLeads.reduce((sum, lead) => sum + (lead.leadPrice || 0), 0);

    const isLeadMarkAsPaidBulkEligible = (lead: Lead) => {
        if (!isLeadBulkPayEligible(lead)) return false;
        const payee = getLeadPayee(lead);
        const available = payee?.alreadyPaidAmount ?? 0;
        return available >= (lead.leadPrice || 0);
    };
    const selectedMarkAsPaidLeads = leads.filter((lead) => selectedLeadIds.includes(lead._id) && isLeadMarkAsPaidBulkEligible(lead));
    const selectedMarkAsPaidPayoutTotal = selectedMarkAsPaidLeads.reduce((sum, lead) => sum + (lead.leadPrice || 0), 0);

    const selectedRejectableLeads = leads.filter((lead) => selectedLeadIds.includes(lead._id) && isLeadBulkRejectEligible(lead));

    const handleBulkPaySelected = async () => {
        if (selectedLeads.length === 0) return;

        const confirmMsg = `Confirmer le paiement de ${selectedLeads.length} lead(s) pour un total de €${selectedPayoutTotal.toFixed(2)} ?`;
        if (!window.confirm(confirmMsg)) return;

        setIsBulkPaying(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(
                `${CONFIG.apiUrl}/admin/leads/pay-bulk`,
                { leadIds: selectedLeads.map((lead) => lead._id) },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            const data = response.data || {};
            const successCount = data.successCount ?? 0;
            const failedCount = data.failedCount ?? 0;
            const failed = Array.isArray(data.failed) ? data.failed : [];

            if (failedCount > 0) {
                const preview = failed.slice(0, 3).map((f: any) => `${f.leadId}: ${f.message}`).join('\n');
                alert(`Paiement terminé. ${successCount} succès, ${failedCount} échec(s).\n${preview}${failedCount > 3 ? '\n...' : ''}`);
            } else {
                alert(`Paiement réussi pour ${successCount} lead(s).`);
            }

            setSelectedLeadIds([]);
            fetchLeads();
        } catch (error: any) {
            alert(error?.response?.data?.message || 'Erreur lors du paiement en masse');
        } finally {
            setIsBulkPaying(false);
        }
    };

    const handleBulkMarkAsPaid = async () => {
        if (selectedMarkAsPaidLeads.length === 0) return;
        const confirmMsg = `Marquer ${selectedMarkAsPaidLeads.length} lead(s) comme payé(s) sans virement Stripe (déduit du solde vendeur) — total €${selectedMarkAsPaidPayoutTotal.toFixed(2)} ?`;
        if (!window.confirm(confirmMsg)) return;

        setIsBulkMarkingAsPaid(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(
                `${CONFIG.apiUrl}/admin/leads/mark-as-paid-bulk`,
                { leadIds: selectedMarkAsPaidLeads.map((lead) => lead._id) },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            const data = response.data || {};
            const successCount = data.successCount ?? 0;
            const failedCount = data.failedCount ?? 0;
            const failed = Array.isArray(data.failed) ? data.failed : [];
            if (failedCount > 0) {
                const preview = failed.slice(0, 3).map((f: any) => `${f.leadId}: ${f.message}`).join('\n');
                alert(`Terminé. ${successCount} succès, ${failedCount} échec(s).\n${preview}${failedCount > 3 ? '\n...' : ''}`);
            } else {
                alert(`${successCount} lead(s) marqué(s) comme payé(s).`);
            }
            setSelectedLeadIds([]);
            fetchLeads();
        } catch (error: any) {
            alert(error?.response?.data?.message || 'Erreur lors du marquage en masse');
        } finally {
            setIsBulkMarkingAsPaid(false);
        }
    };

    const handleBulkRejectApproved = () => {
        if (selectedLeads.length === 0) return;
        setRejectReason('');
        setRejectModal({ type: 'bulkRejectApproved' });
    };

    const handleBulkRejectSelected = () => {
        if (selectedRejectableLeads.length === 0) return;
        setRejectReason('');
        setRejectModal({ type: 'bulkRejectSelected' });
    };

    const closeForwardModal = () => {
        setIsForwardModalOpen(false);
        setForwardingLead(null);
        setAvailableOffers([]);
        setSelectedOfferId('');
        setSelectedOfferType('');
    };

    const handleForwardLead = async () => {
        if (!forwardingLead || !selectedOfferId) return;
        setIsForwarding(true);
        try {
            const token = localStorage.getItem('token');
            await axios.post(
                `${CONFIG.apiUrl}/admin/leads/${forwardingLead._id}/forward`,
                { targetOfferId: selectedOfferId, targetOfferType: selectedOfferType },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            closeForwardModal();
            fetchLeads();
        } catch (error: any) {
            console.error('Error forwarding lead:', error);
            alert(error?.response?.data?.message || 'Erreur lors de la réattribution du lead');
        } finally {
            setIsForwarding(false);
        }
    };

    // The No match tab has its own compact layout (every other tab keeps the full one): no
    // Status/Approval/Tag columns, merged scores, a Batiweb forwarding column, actions in a "⋯" menu.
    const isNoMatchTab = isNoMatchLikeTab(tabFilter);
    const tableColSpan = isNoMatchTab ? 8 : (tabFilter === 'approved' || tabFilter === 'pending') ? 11 : 10;

    // Tags stored by buyer in localStorage (leadTags_map: { [leadId]: string })
    const [leadTagsMap, setLeadTagsMap] = useState<Record<string, string>>({});
    useEffect(() => {
        const stored = localStorage.getItem('leadTags_map');
        if (stored) setLeadTagsMap(JSON.parse(stored));
    }, []);

    return (
        <div className="pt-[50px]">
            <Card extra="p-5">
                <div className="mb-5 flex items-center justify-between gap-2">
                    <div>
                        <div className="text-2xl font-bold text-navy-700 dark:text-white">
                            Leads Management
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                            {total.toLocaleString()} total leads
                        </div>
                    </div>
                </div>

                <form
                    className="mb-4 flex gap-2"
                    onSubmit={(e) => { e.preventDefault(); setSearchQuery(searchInput); setPage(1); }}
                >
                    <input
                        type="text"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        placeholder="Nom, email, téléphone, réponse du lead, vendeur / side seller / acheteur, n° commande, site, formulaire, ID..."
                        className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white dark:placeholder-gray-400"
                    />
                    <button
                        type="submit"
                        className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600"
                    >
                        Rechercher
                    </button>
                    {searchQuery && (
                        <button
                            type="button"
                            onClick={() => { setSearchInput(''); setSearchQuery(''); setPage(1); }}
                            className="flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-2 text-sm hover:bg-gray-100 dark:border-navy-600 dark:hover:bg-navy-700"
                        >
                            <MdClose size={16} /> Effacer
                        </button>
                    )}
                </form>

                <div className="mb-5 flex gap-1 border-b border-gray-200 dark:border-navy-600">
                    {([
                        { value: 'all', label: 'Tous' },
                        { value: 'pending', label: 'En attente' },
                        { value: 'conflict', label: 'Conflit' },
                        { value: 'approved', label: 'Approuvés' },
                        { value: 'rejected', label: 'Rejetés' },
                        { value: 'paid', label: 'Payés' },
                        { value: 'invalid', label: 'Invalides' },
                        { value: 'no_match', label: 'No match' },
                        { value: 'unsold', label: 'Invendus' },
                        { value: 'blocked', label: 'Bloqués' },
                        { value: 'duplicate', label: 'Dupliqués' },
                        { value: 'debug', label: 'Debug' },
                    ] as { value: any; label: string }[]).map((tab) => (
                        <button
                            key={tab.value}
                            onClick={() => handleTabClick(tab.value)}
                            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
                                tabFilter === tab.value
                                    ? 'border-brand-500 text-brand-500 dark:border-brand-400 dark:text-brand-400'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                            }`}
                        >
                            {tab.label}
                            {!!tabNewCounts[tab.value] && (
                                <span className="rounded-full bg-red-600 px-1.5 py-0.5 text-[10px] font-bold leading-none text-white">
                                    {tabNewCounts[tab.value] > 99 ? '99+' : tabNewCounts[tab.value]}
                                </span>
                            )}
                        </button>
                    ))}
                </div>

                <div className="mb-4 flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-2">
                        <label className="text-xs font-medium text-gray-600 dark:text-gray-400">Secteur</label>
                        <SearchSelect
                            options={availableIndustries.map((ind) => ({ value: ind.name, label: ind.name }))}
                            value={industryFilter}
                            onChange={(value) => { setIndustryFilter(value); setSubIndustryFilter(''); setPage(1); }}
                            placeholder="Tous"
                            searchPlaceholder="Rechercher un secteur"
                            clearable
                            className="w-48"
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <label className="text-xs font-medium text-gray-600 dark:text-gray-400">Produit</label>
                        {/* Tous les produits (ceux du secteur choisi, le cas échéant) : choisir un produit fixe aussi son secteur. */}
                        <SearchSelect
                            options={productOptions(availableIndustries, industryFilter || undefined)}
                            value={subIndustryFilter ? `${industryFilter}|${subIndustryFilter}` : ''}
                            onChange={(value) => {
                                const [industry, subIndustry] = value ? value.split('|') : [industryFilter, ''];
                                setIndustryFilter(industry);
                                setSubIndustryFilter(subIndustry);
                                setPage(1);
                            }}
                            placeholder="Tous"
                            searchPlaceholder="Rechercher un produit ou un secteur"
                            clearable
                            className="w-56"
                        />
                    </div>
                    {isNoMatchTab && (
                        <div className="flex items-center gap-2">
                            <label className="text-xs font-medium text-gray-600 dark:text-gray-400">Batiweb</label>
                            <SearchSelect
                                options={BATIWEB_FILTER_OPTIONS}
                                value={batiwebStateFilter}
                                onChange={(value) => { setBatiwebStateFilter(value); setPage(1); }}
                                placeholder="Tous les statuts"
                                searchPlaceholder="Rechercher un statut"
                                clearable
                                className="w-52"
                            />
                        </div>
                    )}
                    <div className="relative flex items-center gap-2" ref={regionDropdownRef}>
                        <label className="text-xs font-medium text-gray-600 dark:text-gray-400">Région</label>
                        <button
                            type="button"
                            onClick={() => setIsRegionDropdownOpen((prev) => !prev)}
                            className="min-w-[160px] rounded-lg border border-gray-300 px-3 py-1.5 text-left text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                        >
                            {regionFilter.length === 0
                                ? '🇫🇷 Toute la France'
                                : regionFilter.length === 1
                                ? regionFilter[0]
                                : `${regionFilter.length} régions sélectionnées`}
                        </button>
                        {isRegionDropdownOpen && (
                            <div className="absolute left-0 top-full z-10 mt-1 w-72 rounded-lg border border-gray-300 bg-white shadow-lg dark:border-navy-600 dark:bg-navy-800">
                                <div className="p-2">
                                    <input
                                        type="text"
                                        autoFocus
                                        value={regionSearchInput}
                                        onChange={(e) => setRegionSearchInput(e.target.value)}
                                        placeholder="Rechercher une région ou un département..."
                                        className="w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white dark:placeholder-gray-400"
                                    />
                                </div>
                                <div className="max-h-64 overflow-y-auto px-2 pb-2">
                                    {regionFilter.length > 0 && (
                                        <button
                                            type="button"
                                            onClick={() => { setRegionFilter([]); setPage(1); }}
                                            className="mb-1 w-full rounded px-2 py-1 text-left text-xs font-medium text-brand-500 hover:bg-gray-100 dark:hover:bg-navy-700"
                                        >
                                            Effacer la sélection
                                        </button>
                                    )}
                                    {NO_MATCH_REGION_OPTIONS
                                        .filter((area) => normalizeIndustryLabel(area).includes(normalizeIndustryLabel(regionSearchInput)))
                                        .map((area) => (
                                            <label
                                                key={area}
                                                className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm hover:bg-gray-100 dark:text-white dark:hover:bg-navy-700"
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={regionFilter.includes(area)}
                                                    onChange={() => {
                                                        setRegionFilter((prev) => prev.includes(area) ? prev.filter((a) => a !== area) : [...prev, area]);
                                                        setPage(1);
                                                    }}
                                                    className="rounded border-gray-300"
                                                />
                                                {area}
                                            </label>
                                        ))}
                                </div>
                            </div>
                        )}
                    </div>
                    {(industryFilter || subIndustryFilter || batiwebStateFilter || regionFilter.length > 0) && (
                        <button
                            type="button"
                            onClick={() => { setIndustryFilter(''); setSubIndustryFilter(''); setBatiwebStateFilter(''); setRegionFilter([]); setPage(1); }}
                            className="flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-1.5 text-xs hover:bg-gray-100 dark:border-navy-600 dark:hover:bg-navy-700"
                        >
                            <MdClose size={14} /> Effacer filtres
                        </button>
                    )}
                    {isNoMatchTab && industryFilter && subIndustryFilter && regionFilter.length > 0 && (
                        <button
                            type="button"
                            onClick={handleExportNoMatchCsv}
                            disabled={isExportingCsv}
                            className="flex items-center gap-1.5 rounded-lg border border-emerald-400 bg-emerald-500 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            <MdFileDownload size={14} />
                            {isExportingCsv ? 'Export en cours...' : 'Exporter en CSV'}
                        </button>
                    )}
                </div>

                {tabFilter === 'approved' && commissionStats && (
                    <div className="mb-5 grid grid-cols-3 gap-4">
                        {[
                            { label: '7 derniers jours', value: commissionStats.days7 },
                            { label: '30 derniers jours', value: commissionStats.days30 },
                            { label: '90 derniers jours', value: commissionStats.days90 },
                        ].map(({ label, value }) => (
                            <div key={label} className="rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-navy-600 dark:bg-navy-700">
                                <div className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</div>
                                <div className="mt-1 text-2xl font-bold text-navy-700 dark:text-white">
                                    €{value.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </div>
                                <div className="mt-0.5 text-xs text-gray-400 dark:text-gray-500">Commission nette (hors affiliés)</div>
                            </div>
                        ))}
                    </div>
                )}

                {tabFilter === 'approved' && (
                    <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 dark:border-emerald-900/60 dark:bg-emerald-900/20">
                        <div>
                            <div className="text-sm font-semibold text-emerald-800 dark:text-emerald-300">
                                Paiement groupé des leads approuvés
                            </div>
                            <div className="text-xs text-emerald-700 dark:text-emerald-400">
                                {selectedLeads.length} sélectionné(s) • Total vendeur: €{selectedPayoutTotal.toFixed(2)}
                            </div>
                        </div>
                        <div className="text-xs font-medium text-emerald-700 dark:text-emerald-400">
                            Sélectionnez des lignes puis utilisez le bouton d'action dédié.
                        </div>
                    </div>
                )}

                {tabFilter === 'pending' && (
                    <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 dark:border-red-900/60 dark:bg-red-900/20">
                        <div>
                            <div className="text-sm font-semibold text-red-800 dark:text-red-300">
                                Rejet groupé des leads en attente
                            </div>
                            <div className="text-xs text-red-700 dark:text-red-400">
                                {selectedRejectableLeads.length} sélectionné(s)
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={handleBulkRejectSelected}
                            disabled={isBulkRejecting || selectedRejectableLeads.length === 0}
                            className="flex items-center gap-1.5 rounded-lg border border-red-400 bg-red-500 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-red-600 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            <MdBlock size={14} />
                            {isBulkRejecting ? 'Rejet en cours...' : 'Rejeter la sélection'}
                        </button>
                    </div>
                )}

                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-gray-200 dark:border-navy-600">
                                {tabFilter === 'approved' && (
                                    <th className="pb-3 pr-2 text-left">
                                        <input
                                            type="checkbox"
                                            checked={allPayableSelected}
                                            disabled={payableLeadIdsOnPage.length === 0 || isBulkPaying}
                                            onChange={toggleSelectAllPayable}
                                            title="Tout sélectionner"
                                            className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                                        />
                                    </th>
                                )}
                                {tabFilter === 'pending' && (
                                    <th className="pb-3 pr-2 text-left">
                                        <input
                                            type="checkbox"
                                            checked={allRejectableSelected}
                                            disabled={rejectableLeadIdsOnPage.length === 0 || isBulkRejecting}
                                            onChange={toggleSelectAllRejectable}
                                            title="Tout sélectionner"
                                            className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                                        />
                                    </th>
                                )}
                                <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                    Order #
                                </th>
                                <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                    Transaction
                                </th>
                                {!isNoMatchTab && (
                                    <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                        Status
                                    </th>
                                )}
                                {!isNoMatchTab && (
                                    <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                        Approval
                                    </th>
                                )}
                                {isNoMatchTab ? (
                                    <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                        Scores
                                    </th>
                                ) : (<>
                                    <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                        Score Leadrs
                                    </th>
                                    <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                        Customer Rate
                                    </th>
                                    <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                        Tag
                                    </th>
                                </>)}
                                <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                    Comment
                                </th>
                                <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                    Prix
                                </th>
                                {isNoMatchTab && (
                                    <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                        Waterfall
                                    </th>
                                )}
                                {isNoMatchTab && (
                                    <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                        Marketplace
                                    </th>
                                )}
                                <th className={`pb-3 text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400 ${isNoMatchTab ? 'text-right' : 'text-left'}`}>
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={tableColSpan} className="py-4 text-center">
                                        Loading...
                                    </td>
                                </tr>
                            ) : leads.length === 0 ? (
                                <tr>
                                    <td colSpan={tableColSpan} className="py-4 text-center">
                                        No leads found
                                    </td>
                                </tr>
                            ) : (
                                leads.map((lead) => (
                                    <React.Fragment key={lead._id}>
                                    <tr
                                        className="border-b border-gray-100 dark:border-navy-700"
                                    >
                                        {tabFilter === 'approved' && (
                                            <td className="py-3 pr-2">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedLeadIds.includes(lead._id)}
                                                    disabled={!isLeadBulkPayEligible(lead) || isBulkPaying}
                                                    onChange={() => toggleLeadSelection(lead._id)}
                                                    className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 disabled:opacity-40"
                                                />
                                            </td>
                                        )}
                                        {tabFilter === 'pending' && (
                                            <td className="py-3 pr-2">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedLeadIds.includes(lead._id)}
                                                    disabled={!isLeadBulkRejectEligible(lead) || isBulkRejecting}
                                                    onChange={() => toggleLeadSelection(lead._id)}
                                                    className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500 disabled:opacity-40"
                                                />
                                            </td>
                                        )}
                                        <td className="py-3 text-xs max-w-xs font-mono">
                                            {/* In the No match tab the order number is just the "NO_MATCH" placeholder, already implied by the tab. */}
                                            {!(isNoMatchTab && (!lead.orderNumber || lead.orderNumber.startsWith('NO_MATCH'))) && (
                                                <div>{lead.orderNumber || lead._id.slice(-8)}</div>
                                            )}
                                            {(() => {
                                                const industry = lead.industry || lead.offerId?.industry || lead.purchaseOrderId?.industry || lead.formTemplateId?.industry;
                                                const subIndustry = lead.subIndustry || lead.offerId?.subIndustry || lead.purchaseOrderId?.subIndustry || lead.formTemplateId?.subIndustry;
                                                if (isNoMatchTab && (subIndustry || industry)) {
                                                    // Product only — the industry stays available on hover.
                                                    return (
                                                        <div className="font-sans">
                                                            <span className="inline-block rounded bg-blue-100 px-1.5 py-0.5 text-[11px] font-medium text-blue-700 dark:bg-blue-900/40 dark:text-blue-300" title={industry || undefined}>
                                                                {subIndustry || industry}
                                                            </span>
                                                        </div>
                                                    );
                                                }
                                                return (industry || subIndustry) ? (
                                                    <div className="mt-1 font-sans">
                                                        {industry && <span className="inline-block rounded bg-blue-100 px-1.5 py-0.5 text-[10px] font-medium text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">{industry}</span>}
                                                        {subIndustry && <span className="ml-1 inline-block rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-600 dark:bg-navy-600 dark:text-gray-300">{subIndustry}</span>}
                                                    </div>
                                                ) : null;
                                            })()}
                                            {lead.leadQualityStatus && lead.leadQualityStatus !== 'valid' && lead.leadQualityStatus !== 'test' && !(isNoMatchTab && lead.leadQualityStatus === 'no_match') && (
                                                <span
                                                    className="mt-1 inline-block rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-bold text-orange-700"
                                                    title={lead.invalidReason || undefined}
                                                >
                                                    {lead.leadQualityStatus === 'no_match' ? 'NO_MATCH' : lead.leadQualityStatus.toUpperCase()}
                                                </span>
                                            )}
                                            {lead.leadQualityStatus === 'no_match' && lead.unsoldAt && tabFilter !== 'unsold' && (
                                                <span
                                                    className="mt-1 ml-1 inline-block rounded-full bg-gray-200 px-2 py-0.5 text-[10px] font-bold text-gray-700"
                                                    title={`Invendu depuis le ${new Date(lead.unsoldAt).toLocaleDateString('fr-FR')}`}
                                                >
                                                    INVENDU
                                                </span>
                                            )}
                                            {lead.leadQualityStatus === 'duplicate' && duplicateSearchValue(lead) && (
                                                <button
                                                    type="button"
                                                    onClick={() => handleShowDuplicates(duplicateSearchValue(lead)!)}
                                                    className="mt-1 ml-1 inline-flex items-center gap-0.5 rounded-full border border-orange-200 px-2 py-0.5 font-sans text-[10px] font-medium text-orange-700 hover:bg-orange-50 dark:border-orange-800 dark:text-orange-300 dark:hover:bg-orange-900/30"
                                                    title={`Rechercher ${duplicateSearchValue(lead)} pour voir tous les leads similaires`}
                                                >
                                                    <MdSearch size={11} /> Voir les doublons
                                                </button>
                                            )}
                                            {lead.isForceBought && (
                                                <span
                                                    className="mt-1 ml-1 inline-block rounded-full bg-purple-100 px-2 py-0.5 text-[10px] font-bold text-purple-700 dark:bg-purple-900/40 dark:text-purple-300"
                                                    title="Acheté en interne par Leadrs via « Forcer l'achat » — invisible pour le vendeur"
                                                >
                                                    ⚡ Forcé
                                                </span>
                                            )}
                                            <div className="mt-1 flex items-center gap-1 font-sans text-[10px] text-gray-400">
                                                <span>...{lead._id.slice(-10)}</span>
                                                <button
                                                    type="button"
                                                    onClick={() => handleCopyLeadId(lead._id)}
                                                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                                                    title="Copier l'ID complet du lead"
                                                >
                                                    {copiedLeadId === lead._id ? <MdCheck size={12} className="text-green-500" /> : <MdContentCopy size={12} />}
                                                </button>
                                            </div>
                                        </td>
                                        <td className="py-3 text-sm">
                                            <div className="flex flex-col gap-2">
                                                {/* Buyer */}
                                                <div className="flex flex-col">
                                                    {lead.buyerId ? (
                                                        <>
                                                            <div className="font-medium text-blue-600 dark:text-blue-400">
                                                                {userDisplayName(lead.buyerId)}
                                                            </div>
                                                            <div className="text-xs text-gray-500">
                                                                A: <button type="button" onClick={() => navigate(`/back-office/users?search=${encodeURIComponent(lead.buyerId.email)}`)} title={isNoMatchTab ? lead.buyerId.email : undefined} className={isNoMatchTab ? 'inline-block max-w-[150px] truncate align-bottom underline text-brand-400 hover:text-brand-500' : 'underline text-brand-400 hover:text-brand-500'}>{lead.buyerId.email}</button>
                                                            </div>
                                                        </>
                                                    ) : lead.helloArtisan?.status === 'sold' ? (
                                                        <>
                                                            <div className="font-medium text-blue-600 dark:text-blue-400">Batiweb</div>
                                                            <div className="text-xs text-gray-500" title={lead.helloArtisan.worktypeLabel ? `Métier : ${lead.helloArtisan.worktypeLabel}` : undefined}>
                                                                A: HelloArtisan{lead.buyerPrice ? ` — €${lead.buyerPrice.toFixed(2)}` : ''}
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <span className="text-gray-500">-</span>
                                                    )}
                                                </div>
                                                {/* Arrow */}
                                                <div className="flex items-center text-gray-400">
                                                    <svg className="h-4 w-4 rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                                                    </svg>
                                                </div>
                                                {/* Side-Seller and/or Seller */}
                                                {(() => {
                                                    const hasSideSeller = lead.sideSellerId && lead.sideSellerId._id !== lead.sellerId?._id;
                                                    
                                                    if (hasSideSeller) {
                                                        // Show both: side-seller (active) and original seller (crossed out)
                                                        return (
                                                            <div className="flex flex-col gap-1.5">
                                                                {/* Side-seller - active lead generator */}
                                                                <div className="flex flex-col">
                                                                    <div className="font-medium text-orange-600 dark:text-orange-400">
                                                                        {userDisplayName(lead.sideSellerId)}
                                                                    </div>
                                                                    <div className="text-xs text-gray-500">
                                                                        S: <button type="button" onClick={() => navigate(`/back-office/users?search=${encodeURIComponent(lead.sideSellerId!.email)}`)} title={isNoMatchTab ? lead.sideSellerId!.email : undefined} className={isNoMatchTab ? 'inline-block max-w-[150px] truncate align-bottom underline text-brand-400 hover:text-brand-500' : 'underline text-brand-400 hover:text-brand-500'}>{lead.sideSellerId!.email}</button>
                                                                    </div>
                                                                </div>
                                                                {/* Original seller - crossed out (linked to offer) */}
                                                                {lead.sellerId && (
                                                                    <div className="flex flex-col opacity-60">
                                                                        <div className="font-medium text-purple-600 dark:text-purple-400 line-through">
                                                                            {userDisplayName(lead.sellerId)}
                                                                        </div>
                                                                        <div className="text-xs text-gray-500 line-through">
                                                                            V: <button type="button" onClick={() => navigate(`/back-office/users?search=${encodeURIComponent(lead.sellerId.email)}`)} title={isNoMatchTab ? lead.sellerId.email : undefined} className={isNoMatchTab ? 'inline-block max-w-[150px] truncate align-bottom underline text-brand-400 hover:text-brand-500' : 'underline text-brand-400 hover:text-brand-500'}>{lead.sellerId.email}</button>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    } else {
                                                        // Show regular seller with V: prefix
                                                        return (
                                                            <div className="flex flex-col">
                                                                {lead.sellerId ? (
                                                                    <>
                                                                        <div className="font-medium text-purple-600 dark:text-purple-400">
                                                                            {userDisplayName(lead.sellerId)}
                                                                        </div>
                                                                        <div className="text-xs text-gray-500">
                                                                            V: <button type="button" onClick={() => navigate(`/back-office/users?search=${encodeURIComponent(lead.sellerId.email)}`)} title={isNoMatchTab ? lead.sellerId.email : undefined} className={isNoMatchTab ? 'inline-block max-w-[150px] truncate align-bottom underline text-brand-400 hover:text-brand-500' : 'underline text-brand-400 hover:text-brand-500'}>{lead.sellerId.email}</button>
                                                                        </div>
                                                                    </>
                                                                ) : (
                                                                    <span className="text-gray-500">-</span>
                                                                )}
                                                            </div>
                                                        );
                                                    }
                                                })()}
                                                {lead.channels && lead.channels.length > 0 && (
                                                    <div className="flex flex-wrap gap-0.5">
                                                        {lead.channels.map((channel, i) => (
                                                            <span
                                                                key={i}
                                                                className="inline-block rounded bg-teal-100 px-1 py-px text-[9px] leading-tight font-medium text-teal-700 dark:bg-teal-900/40 dark:text-teal-300"
                                                            >
                                                                {channel}
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                        {!isNoMatchTab && (<>
                                        <td className="py-3">
                                            <span
                                                className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${getStatusColor(
                                                    lead.status
                                                )}`}
                                            >
                                                {lead.status}
                                            </span>
                                        </td>
                                        <td className="py-3">
                                            <div className="flex flex-col gap-2">
                                                {lead.approvalStatus ? (
                                                    <span
                                                        className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${getApprovalStatusColor(
                                                            lead.approvalStatus
                                                        )}`}
                                                    >
                                                        {lead.approvalStatus === 'conflict' && '🚨 '}
                                                        {lead.approvalStatus}
                                                    </span>
                                                ) : (
                                                    <span className="text-gray-500">-</span>
                                                )}
                                                {(lead.approvalStatus === 'conflict' || lead.approvalStatus === 'pending') && (
                                                    <div className="flex gap-1">
                                                        {lead.approvalStatus === 'conflict' && (
                                                            <button
                                                                onClick={() => handleApprovalStatusChange(lead._id, 'approved')}
                                                                className="rounded bg-green-500 px-2 py-1 text-xs text-white hover:bg-green-600"
                                                                title="Approve lead"
                                                            >
                                                                Envoyer à l'acheteur
                                                            </button>
                                                        )}
                                                        <button
                                                            onClick={() => handleAdminReject(lead._id)}
                                                            className="rounded bg-red-500 px-2 py-1 text-xs text-white hover:bg-red-600"
                                                            title="Reject lead"
                                                        >
                                                            Retour vendeur
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                        </>)}
                                        {isNoMatchTab ? (
                                        <td className="py-3 text-xs whitespace-nowrap">
                                            <div className="flex flex-col gap-0.5">
                                                <span title="Score Leadrs">
                                                    <span className="text-gray-400">Leadrs</span>{' '}
                                                    <span className="font-medium">{lead.leadrsRate >= 0 ? `${lead.leadrsRate}/10` : '-'}</span>
                                                </span>
                                                <span title="Customer Rate">
                                                    <span className="text-gray-400">Client</span>{' '}
                                                    <span className="font-medium">{lead.customerRate >= 0 ? `${lead.customerRate}/10` : '-'}</span>
                                                </span>
                                            </div>
                                        </td>
                                        ) : (<>
                                        <td className="py-3 text-sm font-medium">
                                            {lead.leadrsRate >= 0 ? `${lead.leadrsRate}/10` : '-'}
                                        </td>
                                        <td className="py-3 text-sm font-medium">
                                            {lead.customerRate >= 0 ? `${lead.customerRate}/10` : '-'}
                                        </td>
                                        <td className="py-3 text-xs">
                                            {leadTagsMap[lead._id] ? (
                                                <span className="inline-block rounded-full border bg-blue-50 border-blue-200 px-1.5 py-0.5 text-[10px] font-medium text-blue-700">
                                                    {leadTagsMap[lead._id]}
                                                </span>
                                            ) : (
                                                <span className="text-gray-300">—</span>
                                            )}
                                        </td>
                                        </>)}
                                        <td className="py-3 text-sm text-gray-600 dark:text-gray-400">
                                            {lead.comment || '-'}
                                        </td>
                                        <td className="py-3 text-sm">
                                            <div className="flex flex-col gap-0.5">
                                                <div className="flex gap-2 text-xs">
                                                    <span className="text-purple-600 dark:text-purple-400 font-medium" title="Prix vendeur">
                                                        V: {lead.leadPrice ? `€${lead.leadPrice.toFixed(2)}` : '—'}
                                                    </span>
                                                    <span className="text-blue-600 dark:text-blue-400 font-medium" title="Prix acheteur">
                                                        A: {lead.buyerPrice ? `€${lead.buyerPrice.toFixed(2)}` : '—'}
                                                    </span>
                                                </div>
                                                {lead.buyerPrice && lead.leadPrice ? (
                                                    <div className="flex flex-col gap-0.5">
                                                        <span className="text-xs font-bold text-green-600 dark:text-green-400">
                                                            +€{(lead.buyerPrice - lead.leadPrice).toFixed(2)}
                                                        </span>
                                                        {lead.marginBreakdown && (
                                                            <div className="flex flex-col gap-0.5 text-[10px]">
                                                                <span className="text-gray-400">{(lead.marginBreakdown.base10 / lead.leadPrice * 100).toFixed(1)}%: €{lead.marginBreakdown.base10.toFixed(2)}</span>
                                                                {lead.marginBreakdown.sellerCoupon > 0 && (
                                                                    <span className="text-purple-500" title={`Code: ${lead.marginBreakdown.sellerCouponCode}`}>V: +€{lead.marginBreakdown.sellerCoupon.toFixed(2)}</span>
                                                                )}
                                                                {lead.marginBreakdown.buyerCoupon > 0 && (
                                                                    <span className="text-blue-500" title={`Code: ${lead.marginBreakdown.buyerCouponCode}`}>A: +€{lead.marginBreakdown.buyerCoupon.toFixed(2)}</span>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : null}
                                            </div>
                                        </td>
                                        {isNoMatchTab && (
                                            <td className="py-3 pr-2">
                                                <WaterfallJourneyCell
                                                    journey={lead.journey}
                                                    showActions={!!lead.batiwebForward && BATIWEB_ACTION_STATES.includes(lead.batiwebForward.state) && lead.journey?.step === 'bourse'}
                                                >
                                                <BatiwebForwardCell
                                                    leadId={lead._id}
                                                    forward={lead.batiwebForward}
                                                    industries={availableIndustries}
                                                    onPatch={(patch) =>
                                                        setLeads((prev) =>
                                                            prev.map((l) =>
                                                                l._id === lead._id && l.batiwebForward
                                                                    ? { ...l, batiwebForward: { ...l.batiwebForward, ...patch } }
                                                                    : l
                                                            )
                                                        )
                                                    }
                                                    onSent={fetchLeads}
                                                />
                                                </WaterfallJourneyCell>
                                            </td>
                                        )}
                                        {isNoMatchTab && (
                                            <td className="py-3 pr-2">
                                                <MarketplaceVisibilityCell marketplace={lead.marketplace} />
                                            </td>
                                        )}
                                        {isNoMatchTab ? (
                                        <td className="py-3 pl-2">
                                            <div className="mb-1.5 whitespace-nowrap text-right text-xs text-gray-400">
                                                {new Date(lead.createdAt).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' })}
                                                {' '}
                                                {new Date(lead.createdAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                            </div>
                                            {(() => {
                                                const BAD_ORDER_NUMBERS = ['INVALID', 'NO_MATCH', 'BLOCKED', 'INVALID_SIDE_SELLER', 'NO_MATCH_SIDE_SELLER'];
                                                const isNonQualified =
                                                    (lead.leadQualityStatus && lead.leadQualityStatus !== 'valid' && lead.leadQualityStatus !== 'test') ||
                                                    (!lead.leadQualityStatus && BAD_ORDER_NUMBERS.includes(lead.orderNumber));
                                                // Rejected / conflict leads are handled with the approval CTAs only
                                                // ("Envoyer à l'acheteur" / "Retour vendeur"), never reassigned from here.
                                                const isRejectedOrConflict = lead.approvalStatus === 'conflict' || lead.approvalStatus === 'rejected';
                                                const canReassign = isNonQualified && !isRejectedOrConflict && !lead.clonedToLeadId;

                                                // Ordered by importance: the first one becomes the visible button, the rest go in the "⋯" menu.
                                                const actions: RowAction[] = [];
                                                let balanceHint: React.ReactNode = null;

                                                if (!lead.paidAt && lead.leadPrice > 0 && (lead.approvalStatus === 'pending' || lead.approvalStatus === 'approved')) {
                                                    const payee = getLeadPayee(lead);
                                                    const alreadyPaid = payee?.alreadyPaidAmount ?? 0;
                                                    const leadPrice = lead.leadPrice || 0;
                                                    const isInsufficient = alreadyPaid > 0 && alreadyPaid < leadPrice;
                                                    if (alreadyPaid > 0 && !isInsufficient) {
                                                        balanceHint = <span className="text-[10px] font-semibold text-amber-600 dark:text-amber-400">€{alreadyPaid.toFixed(2)} restant</span>;
                                                        actions.push({
                                                            key: 'mark-paid',
                                                            label: 'Marquer payé',
                                                            icon: <MdCheck size={14} />,
                                                            tone: 'warning',
                                                            onClick: () => handleMarkAsPaid(lead),
                                                            loading: markingAsPaidLeadId === lead._id,
                                                            disabled: isBulkPaying,
                                                            title: `Marquer payé — déduit du montant déjà payé (€${alreadyPaid.toFixed(2)} restant)`,
                                                        });
                                                    } else {
                                                        if (isInsufficient) {
                                                            balanceHint = (
                                                                <span className="text-[10px] font-semibold text-orange-500" title={`Solde insuffisant : €${alreadyPaid.toFixed(2)} pour €${leadPrice.toFixed(2)} requis`}>
                                                                    ⚠ €{alreadyPaid.toFixed(2)} restant
                                                                </span>
                                                            );
                                                        }
                                                        actions.push({
                                                            key: 'pay',
                                                            label: 'Payer',
                                                            icon: <MdPayment size={14} />,
                                                            tone: 'success',
                                                            onClick: () => handlePayLead(lead),
                                                            loading: payingLeadId === lead._id,
                                                            disabled: isBulkPaying,
                                                            title: 'Payer le vendeur manuellement',
                                                        });
                                                    }
                                                }
                                                if (lead.leadQualityStatus === 'no_match' && !!lead.forceBuyPrice) {
                                                    actions.push({
                                                        key: 'force-buy',
                                                        label: `Forcer l'achat (€${lead.forceBuyPrice.toFixed(2)})`,
                                                        icon: <MdShoppingCart size={14} />,
                                                        tone: 'purple',
                                                        onClick: () => handleForceBuy(lead),
                                                        loading: forceBuyingLeadId === lead._id,
                                                        title: 'Achète ce lead en interne (Leadrs) pour payer le vendeur immédiatement, sans acheteur réel',
                                                    });
                                                }
                                                if (canReassign) {
                                                    actions.push({
                                                        key: 'reassign',
                                                        label: 'Réattribuer',
                                                        icon: <MdForward size={14} />,
                                                        tone: 'warning',
                                                        onClick: () => handleOpenForward(lead),
                                                        title: 'Réattribuer ce lead à une autre offre',
                                                    });
                                                }
                                                if (lead.leadQualityStatus === 'no_match') {
                                                    actions.push({
                                                        key: 'debug',
                                                        label: 'Debug du matching',
                                                        icon: <MdBugReport size={14} />,
                                                        tone: 'neutral',
                                                        onClick: () => handleOpenDebug(lead),
                                                        title: "Voir les offres actives et pourquoi ce lead n'a matché avec aucune",
                                                    });
                                                }
                                                if (lead.approvalStatus === 'approved' || lead.paidAt) {
                                                    actions.push({
                                                        key: 'reject',
                                                        label: lead.paidAt ? 'Rejeter & rembourser' : 'Rejeter',
                                                        icon: <MdCancel size={14} />,
                                                        tone: 'danger',
                                                        onClick: () => handleRejectAccepted(lead),
                                                        title: lead.paidAt ? 'Rejeter et annuler le virement Stripe' : 'Rejeter ce lead approuvé',
                                                    });
                                                }

                                                const isExpanded = expandedLeadIds.has(lead._id);
                                                // Destructive actions go last, below a separator.
                                                const orderedActions = [...actions.filter((a) => a.tone !== 'danger'), ...actions.filter((a) => a.tone === 'danger')];

                                                return (
                                                    <div className="flex flex-col items-end gap-1">
                                                        {(balanceHint || lead.clonedToLeadId) && (
                                                            <div className="flex flex-col items-end">
                                                                {balanceHint}
                                                                {lead.clonedToLeadId && (
                                                                    <span className="text-[10px] font-medium text-gray-400" title="Ce lead a déjà été réattribué">Déjà cloné</span>
                                                                )}
                                                            </div>
                                                        )}
                                                        <div className="flex items-center gap-1">
                                                            {/* "Détails" is the only CTA kept out of the "⋯" menu. */}
                                                            <button
                                                                type="button"
                                                                onClick={() => toggleLeadDetails(lead._id)}
                                                                title={isExpanded ? 'Masquer les détails' : 'Voir les détails'}
                                                                aria-expanded={isExpanded}
                                                                className={`inline-flex h-7 items-center gap-0.5 rounded-md border pl-2 pr-1 text-xs font-medium transition ${isExpanded ? 'border-brand-200 bg-brand-50 text-brand-600 dark:border-brand-400/40 dark:bg-brand-400/10 dark:text-brand-300' : 'border-gray-200 text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:border-navy-600 dark:text-gray-300 dark:hover:bg-navy-700 dark:hover:text-white'}`}
                                                            >
                                                                Détails
                                                                {isExpanded ? <MdExpandLess size={16} /> : <MdExpandMore size={16} />}
                                                            </button>
                                                            <RowActions actions={orderedActions} />
                                                        </div>
                                                    </div>
                                                );
                                            })()}
                                        </td>
                                        ) : (
                                        <td className="py-3">
                                            <div className="mb-1.5 text-xs text-gray-400">
                                                {new Date(lead.createdAt).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' })}
                                                {' '}
                                                {new Date(lead.createdAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                            </div>
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() => toggleLeadDetails(lead._id)}
                                                    className="flex items-center gap-1 rounded bg-blue-500 px-2 py-1 text-xs text-white hover:bg-blue-600"
                                                    title="Afficher/masquer les informations du lead"
                                                >
                                                    Détails
                                                    {expandedLeadIds.has(lead._id) ? <MdExpandLess size={14} /> : <MdExpandMore size={14} />}
                                                </button>
                                                {!lead.paidAt && lead.leadPrice > 0 && (lead.approvalStatus === 'pending' || lead.approvalStatus === 'approved') && (() => {
                                                    const payee = getLeadPayee(lead);
                                                    const alreadyPaid = payee?.alreadyPaidAmount ?? 0;
                                                    const leadPrice = lead.leadPrice || 0;
                                                    const isInsufficient = alreadyPaid > 0 && alreadyPaid < leadPrice;

                                                    if (alreadyPaid > 0 && !isInsufficient) {
                                                        return (
                                                            <div className="flex flex-col items-start gap-0.5">
                                                                <span className="text-[10px] font-semibold text-amber-600 dark:text-amber-400">
                                                                    €{alreadyPaid.toFixed(2)} restant
                                                                </span>
                                                                <button
                                                                    onClick={() => handleMarkAsPaid(lead)}
                                                                    disabled={markingAsPaidLeadId === lead._id || isBulkPaying}
                                                                    className="rounded bg-amber-500 px-2 py-1 text-xs text-white hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                                                    title={`Marquer payé — déduit du montant déjà payé (€${alreadyPaid.toFixed(2)} restant)`}
                                                                >
                                                                    {markingAsPaidLeadId === lead._id ? '...' : 'Marquer payé'}
                                                                </button>
                                                            </div>
                                                        );
                                                    }
                                                    return (
                                                        <div className="flex flex-col items-start gap-0.5">
                                                            {isInsufficient && (
                                                                <span className="text-[10px] font-semibold text-orange-500" title={`Solde insuffisant : €${alreadyPaid.toFixed(2)} pour €${leadPrice.toFixed(2)} requis`}>
                                                                    ⚠ €{alreadyPaid.toFixed(2)} restant
                                                                </span>
                                                            )}
                                                            <button
                                                                onClick={() => handlePayLead(lead)}
                                                                disabled={payingLeadId === lead._id || isBulkPaying}
                                                                className="rounded bg-green-500 px-2 py-1 text-xs text-white hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                                                title="Payer le vendeur manuellement"
                                                            >
                                                                {payingLeadId === lead._id ? '...' : 'Payer'}
                                                            </button>
                                                        </div>
                                                    );
                                                })()}
                                                {(lead.approvalStatus === 'approved' || lead.paidAt) && (
                                                    <button
                                                        onClick={() => handleRejectAccepted(lead)}
                                                        className="rounded bg-red-500 px-2 py-1 text-xs text-white hover:bg-red-600"
                                                        title={lead.paidAt ? 'Rejeter et annuler le virement Stripe' : 'Rejeter ce lead approuvé'}
                                                    >
                                                        {lead.paidAt ? 'Rejeter & rembourser' : 'Rejeter'}
                                                    </button>
                                                )}
                                                {lead.leadQualityStatus === 'no_match' && !!lead.forceBuyPrice && (
                                                    <button
                                                        onClick={() => handleForceBuy(lead)}
                                                        disabled={forceBuyingLeadId === lead._id}
                                                        className="rounded bg-purple-600 px-2 py-1 text-xs text-white hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-50"
                                                        title="Achète ce lead en interne (Leadrs) pour payer le vendeur immédiatement, sans acheteur réel"
                                                    >
                                                        {forceBuyingLeadId === lead._id ? '...' : `Forcer l'achat (€${lead.forceBuyPrice.toFixed(2)})`}
                                                    </button>
                                                )}
                                                {lead.leadQualityStatus === 'no_match' && (
                                                    <button
                                                        onClick={() => handleOpenDebug(lead)}
                                                        className="rounded bg-gray-600 px-2 py-1 text-xs text-white hover:bg-gray-700"
                                                        title="Voir les offres actives et pourquoi ce lead n'a matché avec aucune"
                                                    >
                                                        Debug
                                                    </button>
                                                )}
                                                {(() => {
                                                    const BAD_ORDER_NUMBERS = ['INVALID', 'NO_MATCH', 'BLOCKED', 'INVALID_SIDE_SELLER', 'NO_MATCH_SIDE_SELLER'];
                                                    const isNonQualified =
                                                        (lead.leadQualityStatus && lead.leadQualityStatus !== 'valid' && lead.leadQualityStatus !== 'test') ||
                                                        (!lead.leadQualityStatus && BAD_ORDER_NUMBERS.includes(lead.orderNumber));
                                                    const isConflict = lead.approvalStatus === 'conflict';
                                                    const isRejected = lead.approvalStatus === 'rejected';
                                                    if (!(isNonQualified || isConflict || isRejected)) return null;
                                                    if (lead.clonedToLeadId) {
                                                        return (
                                                            <span className="rounded bg-gray-200 px-2 py-1 text-xs text-gray-500 dark:bg-navy-600 dark:text-gray-400">
                                                                Déjà cloné
                                                            </span>
                                                        );
                                                    }
                                                    // Rejected / conflict leads keep only the approval CTAs
                                                    // ("Envoyer à l'acheteur" / "Retour vendeur"): no reassignment.
                                                    if (isConflict || isRejected) return null;
                                                    return (
                                                        <button
                                                            onClick={() => handleOpenForward(lead)}
                                                            className="rounded bg-orange-500 px-2 py-1 text-xs text-white hover:bg-orange-600"
                                                            title="Réattribuer ce lead à une autre offre"
                                                        >
                                                            Réattribuer
                                                        </button>
                                                    );
                                                })()}
                                            </div>
                                        </td>
                                        )}
                                    </tr>
                                    {expandedLeadIds.has(lead._id) && (
                                        <tr className="border-b border-gray-100 bg-gray-50 dark:border-navy-700 dark:bg-navy-900">
                                            <td colSpan={tableColSpan} className="px-4 py-3">
                                                <div className="max-h-[50vh] overflow-y-auto pr-1">
                                                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
                                                        <div className="rounded-lg border border-gray-200 bg-white p-3 dark:border-navy-600 dark:bg-navy-800 sm:col-span-2 xl:col-span-4">
                                                            <div className="mb-2 flex items-center justify-between">
                                                                <h4 className="text-[11px] font-bold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                                                                    Informations du lead
                                                                </h4>
                                                                <span className="text-xs">
                                                                    <span className="text-gray-400 dark:text-gray-500">Score Leadrs: </span>
                                                                    <span className="font-medium text-gray-900 dark:text-white">
                                                                        {lead.leadrsRate >= 0 ? `${lead.leadrsRate}/10` : '-'}
                                                                    </span>
                                                                </span>
                                                            </div>
                                                            {lead.body && visibleLeadBody(lead.body).length > 0 ? (
                                                                <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 sm:grid-cols-3 xl:grid-cols-4">
                                                                    {visibleLeadBody(lead.body).map((item, index) => (
                                                                        <div key={index} className="text-xs">
                                                                            <div className="text-gray-400 dark:text-gray-500">{item.field}</div>
                                                                            <div className="truncate font-medium text-gray-900 dark:text-white" title={item.answer}>{item.answer || '-'}</div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            ) : (
                                                                <div className="text-xs text-gray-400 dark:text-gray-500">Aucune donnée</div>
                                                            )}
                                                        </div>


                                                        {/* Origin (channel + IP/geo + landing page) — admin only, hidden from buyer/seller */}
                                                        {(lead.source || lead.ip || lead.country || lead.city || lead.landingPage) && (
                                                            <div className="rounded-lg border border-gray-200 bg-white p-3 text-xs dark:border-navy-600 dark:bg-navy-800 sm:col-span-2 xl:col-span-4">
                                                                <h4 className="mb-2 text-[11px] font-bold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                                                                    Origine
                                                                </h4>
                                                                <div className="flex flex-wrap gap-x-6 gap-y-1 text-gray-700 dark:text-gray-300">
                                                                    {lead.source && (
                                                                        <div>
                                                                            <span className="text-gray-400 dark:text-gray-500">Canal: </span>
                                                                            <span className="font-medium">{leadSourceLabel(lead.source)}</span>
                                                                        </div>
                                                                    )}
                                                                    {lead.landingPage && (
                                                                        <div className="max-w-full">
                                                                            <span className="text-gray-400 dark:text-gray-500">Page d'origine: </span>
                                                                            <a
                                                                                href={lead.landingPage}
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                                className="font-medium text-brand-500 hover:underline dark:text-brand-400"
                                                                                title={lead.landingPage}
                                                                            >
                                                                                {lead.landingPage}
                                                                            </a>
                                                                        </div>
                                                                    )}
                                                                    {lead.ip && (
                                                                        <div>
                                                                            <span className="text-gray-400 dark:text-gray-500">IP: </span>
                                                                            <span className="font-mono">{lead.ip}</span>
                                                                        </div>
                                                                    )}
                                                                    {(lead.country || lead.city) && (
                                                                        <div>
                                                                            <span className="text-gray-400 dark:text-gray-500">Localisation: </span>
                                                                            <span>{[lead.city, lead.country].filter(Boolean).join(', ')}</span>
                                                                        </div>
                                                                    )}
                                                                    {lead.ipDistanceKm != null && (
                                                                        <div>
                                                                            <span className="text-gray-400 dark:text-gray-500">Écart IP / code postal: </span>
                                                                            <span
                                                                                className={`font-medium ${
                                                                                    lead.ipDistanceKm > 100
                                                                                        ? 'text-red-600 dark:text-red-400'
                                                                                        : lead.ipDistanceKm > 30
                                                                                        ? 'text-orange-600 dark:text-orange-400'
                                                                                        : ''
                                                                                }`}
                                                                            >
                                                                                {lead.ipDistanceKm} km
                                                                            </span>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        )}

                                                        {/* RGPD consent — admin only, compact badge with details on hover. Only for iframe-sourced leads. */}
                                                        {lead.gdprConsent && lead.source === 'iframe' && (
                                                            <div className="group relative inline-flex items-center gap-1.5 self-start rounded-full border border-gray-200 bg-white px-2.5 py-1 text-xs dark:border-navy-600 dark:bg-navy-800">
                                                                <MdVerifiedUser className="h-4 w-4 shrink-0 text-gray-400 dark:text-gray-500" />
                                                                <span className="font-medium text-gray-700 dark:text-gray-300">RGPD</span>
                                                                {lead.gdprConsent.consentedAt && (
                                                                    <span className="text-gray-400 dark:text-gray-500">
                                                                        {new Date(lead.gdprConsent.consentedAt).toLocaleDateString('fr-FR')}
                                                                    </span>
                                                                )}
                                                                {lead.gdprConsent.accepted ? (
                                                                    <MdCheckCircle className="h-4 w-4 shrink-0 text-green-500" />
                                                                ) : (
                                                                    <MdCancel className="h-4 w-4 shrink-0 text-red-500" />
                                                                )}

                                                                {/* Full details on hover */}
                                                                <div className="pointer-events-none absolute left-0 top-full z-20 mt-1 hidden w-72 flex-col gap-1 rounded-lg border border-gray-200 bg-white p-3 text-[11px] font-normal text-gray-700 shadow-lg group-hover:flex dark:border-navy-600 dark:bg-navy-900 dark:text-gray-300">
                                                                    <div>
                                                                        <span className="text-gray-400 dark:text-gray-500">Accepté: </span>
                                                                        <span className="font-medium">{lead.gdprConsent.accepted ? 'Oui' : 'Non'}</span>
                                                                    </div>
                                                                    {lead.gdprConsent.consentedAt && (
                                                                        <div>
                                                                            <span className="text-gray-400 dark:text-gray-500">Date: </span>
                                                                            <span>{new Date(lead.gdprConsent.consentedAt).toLocaleString('fr-FR')}</span>
                                                                        </div>
                                                                    )}
                                                                    {lead.gdprConsent.hostName && (
                                                                        <div>
                                                                            <span className="text-gray-400 dark:text-gray-500">Site hôte: </span>
                                                                            <span>{lead.gdprConsent.hostName}</span>
                                                                        </div>
                                                                    )}
                                                                    {lead.gdprConsent.language && (
                                                                        <div>
                                                                            <span className="text-gray-400 dark:text-gray-500">Langue: </span>
                                                                            <span>{lead.gdprConsent.language}</span>
                                                                        </div>
                                                                    )}
                                                                    {lead.gdprConsent.ip && (
                                                                        <div>
                                                                            <span className="text-gray-400 dark:text-gray-500">IP: </span>
                                                                            <span className="font-mono">{lead.gdprConsent.ip}</span>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        )}

                                                        {/* TrustedForm certificate — opens it on TrustedForm (login required). */}
                                                        {lead.gdprConsent?.trustedFormCertUrl && (
                                                            <TrustedFormBadge consent={lead.gdprConsent} createdAt={lead.createdAt} />
                                                        )}

                                                        {/* Invalid reason */}
                                                        {lead.invalidReason && (
                                                            <div className="rounded-lg border border-orange-200 bg-orange-50 p-3 text-xs dark:border-orange-900/50 dark:bg-orange-900/20 sm:col-span-2 xl:col-span-4">
                                                                <h4 className="mb-2 text-[11px] font-bold uppercase tracking-wide text-orange-500 dark:text-orange-400">
                                                                    Raison invalide
                                                                </h4>
                                                                <div className="text-orange-700 dark:text-orange-300">{lead.invalidReason}</div>
                                                            </div>
                                                        )}

                                                        {/* Comment */}
                                                        {lead.comment && (
                                                            <div className="rounded-lg border border-gray-200 bg-white p-3 text-xs dark:border-navy-600 dark:bg-navy-800 sm:col-span-2 xl:col-span-4">
                                                                <h4 className="mb-2 text-[11px] font-bold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                                                                    Commentaire
                                                                </h4>
                                                                <div className="text-gray-700 dark:text-gray-300">{lead.comment}</div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                    </React.Fragment>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="mt-5 flex items-center justify-between">
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                            Page {page} of {totalPages}
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setPage(page - 1)}
                                disabled={page === 1}
                                className="flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:border-navy-600 dark:hover:bg-navy-700"
                            >
                                <MdChevronLeft />
                                Previous
                            </button>
                            <button
                                onClick={() => setPage(page + 1)}
                                disabled={page === totalPages}
                                className="flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:border-navy-600 dark:hover:bg-navy-700"
                            >
                                Next
                                <MdChevronRight />
                            </button>
                        </div>
                    </div>
                )}
            </Card>

            {tabFilter === 'approved' && selectedLeads.length > 0 && (
                <div className="fixed bottom-5 left-4 right-4 z-40 flex flex-col gap-2 md:left-auto md:right-6 md:w-auto">
                    <button
                        type="button"
                        onClick={handleBulkPaySelected}
                        disabled={isBulkPaying || isBulkRejecting || isBulkMarkingAsPaid}
                        className="flex w-full items-center justify-between gap-2 rounded-lg border border-amber-500 bg-amber-300 px-3 py-2 text-left text-amber-950 transition hover:bg-amber-400 dark:border-amber-400 dark:bg-amber-300 dark:text-amber-950 disabled:cursor-not-allowed disabled:opacity-60 md:w-[300px]"
                    >
                        <span className="flex items-center gap-1.5 text-xs font-semibold">
                            <MdPayment size={16} />
                            {isBulkPaying ? 'Paiement en cours...' : 'Payer la sélection'}
                        </span>
                        <span className="rounded border border-amber-700/20 bg-white/75 px-1.5 py-0.5 text-[11px] font-semibold text-amber-900">
                            {selectedLeads.length} • €{selectedPayoutTotal.toFixed(2)}
                        </span>
                    </button>
                    {selectedMarkAsPaidLeads.length > 0 && (
                        <button
                            type="button"
                            onClick={handleBulkMarkAsPaid}
                            disabled={isBulkPaying || isBulkRejecting || isBulkMarkingAsPaid}
                            className="flex w-full items-center justify-between gap-2 rounded-lg border border-blue-500 bg-blue-100 px-3 py-2 text-left text-blue-950 transition hover:bg-blue-200 dark:border-blue-400 dark:bg-blue-900/40 dark:text-blue-100 disabled:cursor-not-allowed disabled:opacity-60 md:w-[300px]"
                        >
                            <span className="flex items-center gap-1.5 text-xs font-semibold">
                                <MdPayment size={16} />
                                {isBulkMarkingAsPaid ? 'Marquage en cours...' : 'Marquer payé (solde)'}
                            </span>
                            <span className="rounded border border-blue-700/20 bg-white/75 px-1.5 py-0.5 text-[11px] font-semibold text-blue-900">
                                {selectedMarkAsPaidLeads.length} • €{selectedMarkAsPaidPayoutTotal.toFixed(2)}
                            </span>
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={handleBulkRejectApproved}
                        disabled={isBulkRejecting || isBulkPaying || isBulkMarkingAsPaid}
                        className="flex w-full items-center justify-between gap-2 rounded-lg border border-red-500 bg-red-100 px-3 py-2 text-left text-red-950 transition hover:bg-red-200 dark:border-red-400 dark:bg-red-900/40 dark:text-red-100 disabled:cursor-not-allowed disabled:opacity-60 md:w-[300px]"
                    >
                        <span className="flex items-center gap-1.5 text-xs font-semibold">
                            <MdBlock size={16} />
                            {isBulkRejecting ? 'Rejet en cours...' : 'Rejeter la sélection'}
                        </span>
                        <span className="rounded border border-red-700/20 bg-white/75 px-1.5 py-0.5 text-[11px] font-semibold text-red-900">
                            {selectedLeads.length}
                        </span>
                    </button>
                </div>
            )}

            {tabFilter === 'pending' && selectedRejectableLeads.length > 0 && (
                <div className="fixed bottom-5 left-4 right-4 z-40 md:left-auto md:right-6 md:w-auto">
                    <button
                        type="button"
                        onClick={handleBulkRejectSelected}
                        disabled={isBulkRejecting}
                        className="flex w-full items-center justify-between gap-2 rounded-lg border border-red-500 bg-red-100 px-3 py-2 text-left text-red-950 transition hover:bg-red-200 dark:border-red-400 dark:bg-red-900/40 dark:text-red-100 disabled:cursor-not-allowed disabled:opacity-60 md:w-[300px]"
                    >
                        <span className="flex items-center gap-1.5 text-xs font-semibold">
                            <MdBlock size={16} />
                            {isBulkRejecting ? 'Rejet en cours...' : 'Rejeter la sélection'}
                        </span>
                        <span className="rounded border border-red-700/20 bg-white/75 px-1.5 py-0.5 text-[11px] font-semibold text-red-900">
                            {selectedRejectableLeads.length}
                        </span>
                    </button>
                </div>
            )}

            {/* Forward Lead Modal */}
            {isForwardModalOpen && forwardingLead && (
                <div className="fixed inset-0 z-[1000] flex items-center justify-center">
                    <div className="absolute inset-0 bg-black bg-opacity-50" onClick={closeForwardModal} />
                    <div className="relative z-10 w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-white dark:bg-navy-800 rounded-lg shadow-xl m-4">
                        <div className="sticky top-0 flex items-center justify-between border-b border-gray-200 dark:border-navy-600 bg-white dark:bg-navy-800 px-6 py-4">
                            <div>
                                <h2 className="text-xl font-bold text-navy-700 dark:text-white">Réattribuer le lead</h2>
                                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                    Lead {forwardingLead.orderNumber || forwardingLead._id.slice(-8)}
                                    {' — '}
                                    <span className="font-medium text-orange-600 uppercase">{forwardingLead.leadQualityStatus || forwardingLead.orderNumber}</span>
                                    {(() => {
                                        const src = forwardingLead.offerId?.industry || forwardingLead.subIndustry || forwardingLead.offerId?.subIndustry
                                            ? forwardingLead.offerId
                                            : forwardingLead.formTemplateId;
                                        const label = [src?.industry, src?.subIndustry].filter(Boolean).join(' / ');
                                        return label ? <span className="ml-2 text-gray-400">· {label}</span> : null;
                                    })()}
                                </p>
                            </div>
                            <button onClick={closeForwardModal} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
                                <MdClose size={24} />
                            </button>
                        </div>

                        <div className="px-6 py-4">
                            {availableOffers.length === 0 ? (
                                <div className="py-8 text-center text-gray-500 dark:text-gray-400">
                                    Aucune offre en cours disponible
                                </div>
                            ) : (() => {
                                const leadSubIndustry = forwardingLead.subIndustry || forwardingLead.offerId?.subIndustry || forwardingLead.purchaseOrderId?.subIndustry || forwardingLead.formTemplateId?.subIndustry;
                                const filtered = (leadSubIndustry && forwardSameSubIndustryOnly)
                                    ? availableOffers.filter((o) => normalizeIndustryLabel(o.subIndustry) === normalizeIndustryLabel(leadSubIndustry))
                                    : availableOffers;
                                return (
                                <>
                                {leadSubIndustry && (
                                    <div className="mb-4 flex items-center justify-between rounded-lg bg-gray-50 dark:bg-navy-700 px-4 py-3">
                                        <div>
                                            <div className="text-sm font-medium text-navy-700 dark:text-white">Même Produit uniquement</div>
                                            <div className="text-xs text-gray-500 dark:text-gray-400">
                                                {forwardSameSubIndustryOnly
                                                    ? `Affiche uniquement les offres « ${leadSubIndustry} »`
                                                    : 'Affiche toutes les offres du secteur, tous produits confondus'}
                                            </div>
                                        </div>
                                        <Switch
                                            color="orange"
                                            checked={forwardSameSubIndustryOnly}
                                            onChange={() => setForwardSameSubIndustryOnly((v) => !v)}
                                        />
                                    </div>
                                )}
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="border-b border-gray-200 dark:border-navy-600">
                                                <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400 w-8"></th>
                                                <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Offre</th>
                                                <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Acheteur</th>
                                                <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Secteur</th>
                                                <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Type</th>
                                                <th className="pb-3 text-right text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Prix vendeur</th>
                                                <th className="pb-3 text-right text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Prix acheteur</th>
                                                <th className="pb-3 text-right text-xs font-bold uppercase tracking-wide text-green-600 dark:text-green-400">Marge</th>
                                                <th className="pb-3 text-right text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Leads</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(() => {
                                                if (filtered.length === 0) {
                                                    return (
                                                        <tr>
                                                            <td colSpan={9} className="py-8 text-center text-gray-500 dark:text-gray-400">
                                                                Aucune offre en cours pour le produit « {leadSubIndustry} »
                                                            </td>
                                                        </tr>
                                                    );
                                                }
                                                return filtered.map((offer) => {
                                                const margin = (offer.buyerPrice || 0) - (offer.sellerPrice || 0);
                                                const isSelected = selectedOfferId === offer._id;
                                                return (
                                                    <tr
                                                        key={offer._id}
                                                        onClick={() => { setSelectedOfferId(offer._id); setSelectedOfferType(offer.offerType); }}
                                                        className={`cursor-pointer border-b border-gray-100 dark:border-navy-700 transition-colors ${
                                                            isSelected
                                                                ? 'bg-orange-50 dark:bg-orange-900/20'
                                                                : 'hover:bg-gray-50 dark:hover:bg-navy-700'
                                                        }`}
                                                    >
                                                        <td className="py-3 pr-3">
                                                            <input
                                                                type="radio"
                                                                checked={isSelected}
                                                                onChange={() => { setSelectedOfferId(offer._id); setSelectedOfferType(offer.offerType); }}
                                                                className="accent-orange-500"
                                                            />
                                                        </td>
                                                        <td className="py-3">
                                                            <div className="font-medium text-navy-700 dark:text-white">{offer.name || '—'}</div>
                                                            {offer.offerNumber && <div className="text-xs text-gray-400 font-mono">{offer.offerNumber}</div>}
                                                        </td>
                                                        <td className="py-3 text-gray-600 dark:text-gray-400">
                                                            {offer.buyer ? (
                                                                <div>
                                                                    <div className="font-medium">{offer.buyer.name} {offer.buyer.surname}</div>
                                                                    <div className="text-xs text-gray-400">{offer.buyer.email}</div>
                                                                </div>
                                                            ) : '—'}
                                                        </td>
                                                        <td className="py-3 text-gray-600 dark:text-gray-400">
                                                            {offer.industry ? (
                                                                <div>
                                                                    <div className="text-xs font-medium">{offer.industry}</div>
                                                                    {offer.subIndustry && <div className="text-xs text-gray-400">{offer.subIndustry}</div>}
                                                                </div>
                                                            ) : '—'}
                                                        </td>
                                                        <td className="py-3">
                                                            <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${
                                                                offer.offerType === 'marketplace'
                                                                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                                                                    : offer.offerType === 'purchaseOrder'
                                                                    ? 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300'
                                                                    : 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300'
                                                            }`}>
                                                                {offer.offerType === 'marketplace' ? 'Marketplace' : offer.offerType === 'purchaseOrder' ? 'Bon de commande' : 'Direct'}
                                                            </span>
                                                        </td>
                                                        <td className="py-3 text-right font-mono">€{(offer.sellerPrice || 0).toFixed(2)}</td>
                                                        <td className="py-3 text-right font-mono">€{(offer.buyerPrice || 0).toFixed(2)}</td>
                                                        <td className="py-3 text-right font-mono font-semibold text-green-600 dark:text-green-400">
                                                            €{margin.toFixed(2)}
                                                        </td>
                                                        <td className="py-3 text-left">
                                                            {(() => {
                                                                const approved = offer.leadApproved ?? 0;
                                                                const pending = offer.leadPending ?? 0;
                                                                const rejected = offer.leadRejected ?? 0;
                                                                const cap = offer.offerType === 'purchaseOrder'
                                                                    ? offer.leadCount ?? 0
                                                                    : Number(offer.weeklyQuantity) || 0;
                                                                const remaining = cap > 0 ? Math.max(0, cap - approved) : null;
                                                                const count = offer.offerType === 'purchaseOrder'
                                                                    ? (offer.totalLeadsDelivered ?? 0)
                                                                    : (offer.leadsGenerated ?? 0);
                                                                return (
                                                                    <div className="flex flex-col gap-0.5 text-xs">
                                                                        <span className="font-semibold text-green-600 dark:text-green-400">
                                                                            Approved: {approved}{cap > 0 ? ` / ${cap}` : ''}
                                                                        </span>
                                                                        <span className="text-yellow-600 dark:text-yellow-400">
                                                                            Pending: {pending}{remaining != null ? ` / ${remaining}` : ''}
                                                                        </span>
                                                                        <span className="text-red-500 dark:text-red-400">
                                                                            Rejected: {rejected}
                                                                        </span>
                                                                        <span className="mt-0.5 text-gray-400">
                                                                            Count: {count}
                                                                        </span>
                                                                        {offer.isAutoSuspended && (
                                                                            <span
                                                                                className="mt-0.5 inline-flex rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-semibold text-orange-800 dark:bg-orange-900 dark:text-orange-200"
                                                                                title="Cap atteint: levé automatiquement quand un lead est rejeté."
                                                                            >
                                                                                ⏸ cap atteint
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                );
                                                            })()}
                                                        </td>
                                                    </tr>
                                                );
                                            });
                                            })()}
                                        </tbody>
                                    </table>
                                </div>
                                </>
                                );
                            })()}

                            {selectedOfferId && (() => {
                                const offer = availableOffers.find(o => o._id === selectedOfferId);
                                if (!offer) return null;
                                const margin = (offer.buyerPrice || 0) - (offer.sellerPrice || 0);
                                return (
                                    <div className="mt-4 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 p-4">
                                        <div className="text-sm font-medium text-green-800 dark:text-green-300 mb-2">Récapitulatif de la réattribution</div>
                                        <div className="grid grid-cols-3 gap-4 text-sm">
                                            <div>
                                                <div className="text-gray-500 dark:text-gray-400">Prix vendeur</div>
                                                <div className="font-mono font-semibold text-navy-700 dark:text-white">€{(offer.sellerPrice || 0).toFixed(2)}</div>
                                            </div>
                                            <div>
                                                <div className="text-gray-500 dark:text-gray-400">Prix acheteur</div>
                                                <div className="font-mono font-semibold text-navy-700 dark:text-white">€{(offer.buyerPrice || 0).toFixed(2)}</div>
                                            </div>
                                            <div>
                                                <div className="text-gray-500 dark:text-gray-400">Marge Leadrs</div>
                                                <div className="font-mono font-bold text-green-600 dark:text-green-400">€{margin.toFixed(2)}</div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>

                        <div className="sticky bottom-0 flex items-center justify-between border-t border-gray-200 dark:border-navy-600 bg-white dark:bg-navy-800 px-6 py-4">
                            <button onClick={closeForwardModal} className="rounded-lg border border-gray-300 dark:border-navy-600 px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-navy-700 transition-colors">
                                Annuler
                            </button>
                            <button
                                onClick={handleForwardLead}
                                disabled={!selectedOfferId || isForwarding}
                                className="rounded-lg bg-orange-500 px-4 py-2 text-sm text-white hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                {isForwarding ? 'Réattribution...' : 'Confirmer la réattribution'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Reject Reason Modal */}
            {rejectModal && (
                <div className="fixed inset-0 z-[1000] flex items-center justify-center">
                    <div className="absolute inset-0 bg-black bg-opacity-50" onClick={closeRejectModal} />
                    <div className="relative z-10 w-full max-w-md m-4 rounded-lg bg-white shadow-xl dark:bg-navy-800">
                        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-navy-600">
                            <h2 className="text-lg font-bold text-navy-700 dark:text-white">
                                {rejectModal.type === 'bulkRejectApproved' || rejectModal.type === 'bulkRejectSelected'
                                    ? 'Rejeter les leads sélectionnés'
                                    : 'Rejeter le lead'}
                            </h2>
                            <button onClick={closeRejectModal} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
                                <MdClose size={22} />
                            </button>
                        </div>

                        <div className="px-6 py-4">
                            {rejectModal.type === 'rejectAccepted' && rejectModal.lead.paidAt && (
                                <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-900/20 dark:text-red-300">
                                    Ce lead a déjà été payé. Le virement Stripe sera annulé.
                                </div>
                            )}
                            {rejectModal.type === 'bulkRejectApproved' && (
                                <div className="mb-3 text-sm text-gray-600 dark:text-gray-400">
                                    {selectedLeads.length} lead(s) sélectionné(s)
                                </div>
                            )}
                            {rejectModal.type === 'bulkRejectSelected' && (
                                <div className="mb-3 text-sm text-gray-600 dark:text-gray-400">
                                    {selectedRejectableLeads.length} lead(s) sélectionné(s)
                                </div>
                            )}

                            <label className="mb-1.5 block text-xs font-medium text-gray-600 dark:text-gray-400">
                                Raison du rejet
                            </label>
                            <select
                                value={rejectReason}
                                onChange={(e) => setRejectReason(e.target.value)}
                                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                            >
                                <option value="">Sélectionner une raison...</option>
                                {REJECTION_REASONS.map((reason) => (
                                    <option key={reason} value={reason}>{reason}</option>
                                ))}
                            </select>
                        </div>

                        <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-6 py-4 dark:border-navy-600">
                            <button
                                onClick={closeRejectModal}
                                className="rounded-lg border border-gray-300 px-4 py-2 text-sm transition-colors hover:bg-gray-100 dark:border-navy-600 dark:hover:bg-navy-700"
                            >
                                Annuler
                            </button>
                            <button
                                onClick={submitRejectModal}
                                disabled={!rejectReason || isBulkRejecting}
                                className="rounded-lg bg-red-500 px-4 py-2 text-sm text-white transition-colors hover:bg-red-600 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                {isBulkRejecting ? 'Rejet en cours...' : 'Confirmer le rejet'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* No-match Debug Modal */}
            {debugLead && (
                <div className="fixed inset-0 z-[1000] flex items-center justify-center">
                    <div className="absolute inset-0 bg-black bg-opacity-50" onClick={closeDebugModal} />
                    <div className="relative z-10 m-4 max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-lg bg-white shadow-xl dark:bg-navy-800">
                        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-200 bg-white px-6 py-4 dark:border-navy-600 dark:bg-navy-800">
                            <h2 className="text-lg font-bold text-navy-700 dark:text-white">
                                Debug: offres actives et raisons de non-match
                            </h2>
                            <button onClick={closeDebugModal} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
                                <MdClose size={22} />
                            </button>
                        </div>

                        <div className="px-6 py-4">
                            {debugLoading ? (
                                <div className="py-8 text-center text-sm text-gray-500">Chargement...</div>
                            ) : debugError ? (
                                <div className="py-8 text-center text-sm text-gray-500">{debugError}</div>
                            ) : debugDiagnostic ? (() => {
                                const runningCandidates = (debugDiagnostic.candidates || []).filter(isRunningCandidate);
                                return (
                                    <>
                                        <div className="mb-4 grid grid-cols-2 gap-3 text-sm md:grid-cols-4">
                                            <div>
                                                <div className="text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">Secteur</div>
                                                <div>
                                                    {debugDiagnostic.criteria.industry || '-'}
                                                    {debugDiagnostic.criteria.subIndustry ? ` / ${debugDiagnostic.criteria.subIndustry}` : ''}
                                                    {debugDiagnostic.criteria.industryRecognized === false && (
                                                        <span className="ml-1 inline-flex rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700 dark:bg-red-900 dark:text-red-200">
                                                            industrie inconnue
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <div>
                                                <div className="text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">Région</div>
                                                <div>{debugDiagnostic.criteria.region || '-'}</div>
                                            </div>
                                            <div>
                                                <div className="text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">Canaux vendeur</div>
                                                <div>{formatCheckValue(debugDiagnostic.criteria.channels)}</div>
                                            </div>
                                            <div>
                                                <div className="text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">Prix minimum</div>
                                                <div>{debugDiagnostic.criteria.minimumPrice != null ? `€${debugDiagnostic.criteria.minimumPrice}` : '-'}</div>
                                            </div>
                                        </div>

                                        {runningCandidates.length === 0 ? (
                                            <div className="py-6 text-center text-sm text-gray-500">
                                                Aucune offre/commande active pour cette industrie/ce produit en ce moment.
                                            </div>
                                        ) : (
                                            <div className="overflow-x-auto">
                                                <table className="w-full">
                                                    <thead>
                                                        <tr className="text-left text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                                            <th className="pb-2 pr-4">Type</th>
                                                            <th className="pb-2 pr-4">Offre</th>
                                                            <th className="pb-2 pr-4">Statut</th>
                                                            <th className="pb-2 pr-4">Prix acheteur</th>
                                                            <th className="pb-2 pr-4">Écart prix</th>
                                                            <th className="pb-2">Raisons du non-match</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {runningCandidates.map((c) => (
                                                            <tr key={`${c.offerType}-${c.offerId}`} className="border-t border-gray-200 dark:border-navy-700">
                                                                <td className="py-2 pr-4 text-xs">{c.offerType}</td>
                                                                <td className="py-2 pr-4 font-mono text-xs">{c.offerNumber || c.name || c.offerId.slice(-8)}</td>
                                                                <td className="py-2 pr-4 text-xs">{c.status}{c.isAutoSuspended ? ' (suspendue)' : ''}</td>
                                                                <td className="py-2 pr-4 text-xs">{c.buyerPrice != null ? `€${c.buyerPrice}` : '-'}</td>
                                                                <td className="py-2 pr-4 text-xs">{c.priceGap != null ? `-€${c.priceGap}` : '-'}</td>
                                                                <td className="py-2 text-xs">
                                                                    {c.failedFilters.length === 0 ? (
                                                                        <span className="text-gray-400">-</span>
                                                                    ) : (
                                                                        <div className="flex flex-col gap-1">
                                                                            {c.failedFilters.map((f) => {
                                                                                const check = c.checks[f];
                                                                                return (
                                                                                    <div key={f} className="flex flex-wrap items-center gap-1.5">
                                                                                        <span className="inline-flex shrink-0 rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700 dark:bg-red-900 dark:text-red-200">
                                                                                            {f}
                                                                                        </span>
                                                                                        {check && (
                                                                                            <span className="text-xs text-gray-500 dark:text-gray-400">
                                                                                                Offre: {formatCheckValue(check.offerValue)} · {check.leadLabel || 'Lead'}: {formatCheckValue(check.leadValue)}
                                                                                            </span>
                                                                                        )}
                                                                                    </div>
                                                                                );
                                                                            })}
                                                                        </div>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                    </>
                                );
                            })() : null}
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
}
